import {
  OpsanioAppliance,
  OpsanioAssemblySite,
  OpsanioNetwork,
  OpsanioDnsEntry,
  OpsanioWireGuard,
  OpsanioNetworkScanner,
  OpsanioForiGateScanner,
  OpsanioBaramundiScanner,
  OpsanioIcingaTwoScanner,
  OpsanioIntuneScanner,
  OpsanioNetdataScanner,
  OpsanioOctopusScanner,
  OpsanioServers,
  OpsanioKula,
  OpsanioProxmoxScanner,
  OpsanioSentinalOneScanner,
  OpsanioValues,
  OpsanioAgentValues,
  OpsanioOtherAppliance,
  OpsanioAgentDevices,
  OpsanioStorageConf,
  OpsanioHardware,
  OpsanioHardwareDevice,
  OpsanioEncryption,
  OpsanioCpeNetwork,
  OpsanioVault,
  OpsanioAi,
  OpsanioAiBudgetWindow,
  OpsanioDashboardOidc,
  OpsanioCustomerSettings,
  OpsanioWaves,
  OpsanioServiceModel,
  OpsanioLdap,
  OpsanioFeatures,
  OpsanioRetentions,
  OpsanioGeneralContacts,
  OpsanioGeneral,
  OpsanioHardwareCpu,
  OpsanioHardwareRam,
  OpsanioHardwareNic,
  OpsanioHardwareStorageMediaType,
  OpsanioHardwareStorage,
  OpsanioPoc,
  OpsanioAgentBgp,
  OpsanioScoping,
  OpsanioScopingNote,
  OpsanioLocationValue,
  OpsanioProbe,
  OpsanioSwitch,
  OpsanioDac,
} from "../../questionnaires";
import {
  calculateHostIP,
  calculatePrivateMacAddresses,
  extractValue,
  getItemsByKeyPrefix,
  getYesNoAsBoolean,
  getLastKeyValue,
  removeKeys,
  getKeyAsYesNoBoolean,
  gibToBytes,
  getArrayByKeyPrefix,
  isValidEmail,
} from "../";
// From the leaf module, not the `src/questionnaires` barrel: this parser is loaded by
// server code and by the node:test harness, and a value import through that barrel
// drags the React app graph (navigation/*.tsx) into them.
import { isValidCIDR, isValidVlan } from "../ip/ip";
import { NetBoxDeviceWithInventory, Vpc } from "../../auth";
// Directly, not via the auth barrel: that barrel pulls in keycloak, and a VALUE
// import of it would drag browser-only code into every server-side consumer.
// The existing type imports above survive only because they are erased.
import { OPSANIO_HARDWARE_DEFECT_TAG } from "../../auth/types/netbox/hardware";

type OpsanioSizing = "small" | "medium" | "large";
interface OpsanioSizeValues {
  opensearch_search_instances: number;
  opensearch_master_instances: number;
  opensearch_master_cpu: number;
  opensearch_master_ram: number;
  cpu_socket_count: number;
  opensearch_client_cpu: number;
  opensearch_client_ram: number;
  opensearch_client_instances: number;
  opensearch_search_cpu: number;
  opensearch_search_ram: number;
  opensearch_hot_per_node: number;
  opensearch_hot_cpu: number;
  opensearch_hot_ram: number;
  opensearch_warm_per_node: number;
  opensearch_warm_cpu: number;
  opensearch_warm_ram: number;
  opensearch_cold_per_node: number;
  opensearch_cold_cpu: number;
  opensearch_cold_ram: number;
}
const opsanioSizingMap: Map<OpsanioSizing, OpsanioSizeValues> = new Map([
  [
    "small",
    {
      opensearch_master_instances: 3,
      opensearch_master_cpu: 650,
      opensearch_master_ram: 8192,
      cpu_socket_count: 5,
      opensearch_client_cpu: 5000,
      opensearch_client_ram: 10240,
      opensearch_client_instances: 2,
      opensearch_search_cpu: 4000,
      opensearch_search_ram: 10240,
      opensearch_search_instances: 2,
      opensearch_hot_per_node: 2,
      opensearch_hot_cpu: 2500,
      opensearch_hot_ram: 18432,
      opensearch_warm_per_node: 2,
      opensearch_warm_cpu: 900,
      opensearch_warm_ram: 15360,
      opensearch_cold_per_node: 1,
      opensearch_cold_cpu: 900,
      opensearch_cold_ram: 6144,
    },
  ],
  [
    "medium",
    {
      opensearch_master_instances: 3,
      // The chart requests sizing/2 and derives node.processors from the full
      // value, so these are both the request and the JVM's processor count.
      // Measured on a medium fleet: masters hold 63m against the 400m they
      // reserved, clients 16-28m against 3000m. Hot stays at 3000 - it is the
      // only tier whose 1500m request matches what it uses.
      opensearch_master_cpu: 400,
      opensearch_master_ram: 8192,
      cpu_socket_count: 6,
      // 2000 and not lower: node.processors is sizing/1000, so 1600 would leave
      // a coordinating node 1 processor and 2 search threads.
      opensearch_client_cpu: 2000,
      opensearch_client_ram: 12288,
      opensearch_client_instances: 2,
      opensearch_search_cpu: 4000,
      opensearch_search_ram: 12288,
      opensearch_search_instances: 2,
      opensearch_hot_per_node: 2,
      opensearch_hot_cpu: 3000,
      opensearch_hot_ram: 24576,
      opensearch_warm_per_node: 2,
      opensearch_warm_cpu: 1000,
      opensearch_warm_ram: 24576,
      opensearch_cold_per_node: 1,
      opensearch_cold_cpu: 1000,
      opensearch_cold_ram: 8192,
    },
  ],
  [
    "large",
    {
      opensearch_master_instances: 3,
      opensearch_master_cpu: 1000,
      opensearch_master_ram: 8192,
      cpu_socket_count: 8,
      opensearch_client_cpu: 8000,
      opensearch_client_ram: 12288,
      opensearch_client_instances: 4,
      opensearch_search_cpu: 4000,
      opensearch_search_ram: 16384,
      opensearch_search_instances: 4,
      opensearch_hot_per_node: 8,
      opensearch_hot_cpu: 4000,
      opensearch_hot_ram: 32768,
      opensearch_warm_per_node: 4,
      opensearch_warm_cpu: 1500,
      opensearch_warm_ram: 12288,
      opensearch_cold_per_node: 2,
      opensearch_cold_cpu: 1500,
      opensearch_cold_ram: 6144,
    },
  ],
]);

export const getLetterFromNumber = (
  num: number,
  uppercase: boolean = false,
): string => {
  if (num < 1 || num > 26) {
    throw new Error("Number must be between 1 and 26");
  }

  const alphabet = uppercase
    ? "ABCDEFGHIJKLMNOPQRSTUVWXYZ"
    : "abcdefghijklmnopqrstuvwxyz";

  return alphabet[num - 1];
};

/**
 * Placeholder CIDR emitted when no VPC has been selected in the `network_vpc`
 * question. It passes `isValidCIDR` (it is a syntactically valid prefix), so
 * consumers must compare against this constant instead of relying on CIDR
 * validation to catch an unselected VPC.
 */
export const VPC_CIDR_UNSET = "0.0.0.0/0";

export const getVpcValues = (obj: Record<string, any>): OpsanioNetwork => {
  const values = getItemsByKeyPrefix(obj, "networks.vpc.");
  const vpcObject =
    typeof values.networkVpc === "string" && values.networkVpc.trim() !== ""
      ? JSON.parse(values.networkVpc)
      : {
          anketa: "",
          bits: 0,
          cidr: VPC_CIDR_UNSET,
          first: "0.0.0.0",
          last: "0.0.0.0",
          isDeployed: false,
          name: "",
          netBox: "",
          owner: "",
          project: "",
          provider: "",
          stage: "",
          tenantId: 0,
        };
  let vlan = vpcObject.tenantId;
  let cidr = vpcObject.cidr;
  let bits = vpcObject.bits;

  // Legacy fallbacks: no current catalog question emits networks.vpc.vpc_vlan or
  // networks.vpc.vpc_network (the `network_vpc` ComplexQuestion replaced them), but
  // questionnaires saved under the older catalog may still carry those facts.
  // Guard on validity, not on the exact placeholder value - a stored VPC object
  // whose tenantId is undefined/null left `vlan === 0` false and skipped the
  // fallback entirely.
  if (!isValidVlan(vlan) && isValidVlan(parseFloat(values.vpcVlan))) {
    vlan = parseFloat(values.vpcVlan);
  }
  if ((cidr === VPC_CIDR_UNSET || !isValidCIDR(cidr)) && values.vpcNetwork) {
    cidr = values.vpcNetwork;
  }
  const legacyBits = parseFloat(values.vpcNetwork?.split("/")[1]);
  if (
    (!Number.isInteger(bits) || bits === 0) &&
    Number.isInteger(legacyBits) &&
    legacyBits > 0
  ) {
    bits = legacyBits;
  }

  const parsed: OpsanioNetwork = {
    name: "VPC",
    cidr: cidr,
    vlan: vlan,
    prefix: bits,
  };
  return parsed;
};

export const getExanioNetwork = (obj: Record<string, any>): OpsanioNetwork => {
  const values: Record<string, any> = getItemsByKeyPrefix(
    obj,
    "networks.exanio_cloud.",
  );
  const parsed: OpsanioNetwork = {
    name: "exanio",
    cidr: values.exanioCloudNetwork,
    gw: calculateHostIP(values.exanioCloudNetwork, 1),
    vlan: parseFloat(values.exanioCloudVlan),
    prefix: parseFloat(values.exanioCloudNetwork?.split("/")[1]),
  };
  return parsed;
};

export const getVaultValues = (obj: Record<string, any>): OpsanioVault => {
  const values = getItemsByKeyPrefix(obj, "vault.");

  const vault: OpsanioVault = {
    address: values.vaultAddress ? values.vaultAddress : "",
    role: values.vaultRole ? values.vaultRole : "",
    secret: values.vaultSecret ? values.vaultSecret : "",
  };

  return vault;
};

export const getDeploymentWave = (obj: Record<string, any>): OpsanioWaves => {
  const values = getItemsByKeyPrefix(obj, "networks.vpc.");
  if (values && values.wave) {
    return values.wave as OpsanioWaves;
  } else {
    return "wave3";
  }
};

/**
 * Hot node RAM (MiB) once hot is the only tier: the memory freed by scaling
 * warm and cold to zero pays for it, the same size on every cluster
 * (opsanio/documentation#448).
 */
export const OPENSEARCH_HOT_ONLY_RAM = 49152;

export const geSizing = (
  obj: Record<string, any>,
  tiers?: OpsanioFeatures["opensearchTiers"],
): {
  node_size: OpsanioSizing;
  sizing: OpsanioSizeValues;
} => {
  const values = getItemsByKeyPrefix(obj, "networks.vpc.");
  // values.size falsy => medium.
  const node_size = (values && values.size ? values.size : "medium") as OpsanioSizing;
  const sizing = opsanioSizingMap.get(node_size);
  // Copy, never mutate: the preset objects are shared by every cluster.
  if (sizing && tiers && !tiers.warm && !tiers.cold) {
    return {
      node_size,
      sizing: { ...sizing, opensearch_hot_ram: OPENSEARCH_HOT_ONLY_RAM },
    };
  }
  return { node_size, sizing };
};

export const getServiceModel = (
  obj: Record<string, any>,
): OpsanioServiceModel => {
  const values = getItemsByKeyPrefix(obj, "networks.vpc.");
  if (
    values.service &&
    typeof values.service === "string" &&
    values.service.trim() !== ""
  ) {
    return values.service as OpsanioServiceModel;
  } else {
    return "medium";
  }
};

export const getOpsanioContact = async (
  obj: Record<string, any>,
  getEmail?: (id: string) => Promise<string>,
): Promise<OpsanioGeneral> => {
  const values = getItemsByKeyPrefix(obj, "general.");
  const contactsRaw = getItemsByKeyPrefix(values, "contacts.");
  const customerRaw = getItemsByKeyPrefix(values, "customers.");
  const dateRaw = getItemsByKeyPrefix(values, "date.");
  const startDate = dateRaw["dateStart"] ?? "";
  const endDate = dateRaw["dateEnd"] ?? "";
  const parseDate = (dateStr: string) => {
    if (!dateStr || dateStr.trim() === "") return "";
    const [day, month, year] = dateStr.trim().split(".");
    return new Date(`${year}-${month}-${day}`).toISOString();
  };
  const poc: OpsanioPoc = {
    allowedAsRefCustomer: getYesNoAsBoolean(values["information.ref"]) || false,
    wantPoc: getYesNoAsBoolean(values["information.poc"]) || false,
    pocSuccess: getYesNoAsBoolean(values["information.pocSuccess"]) || false,
  };
  if (!getEmail)
    return {
      contacts: {
        sales: [],
        technical: [],
        customer: [],
        updateNotifications: [],
      },
      dates: {
        end: parseDate(endDate),
        start: parseDate(startDate),
      },
      poc,
    };

  const technicalIds =
    contactsRaw["technicalContact"]
      ?.split(",")
      .map((id: string) => id.trim())
      .filter(Boolean) ?? [];

  const salesIds =
    contactsRaw["salesContact"]
      ?.split(",")
      .map((id: string) => id.trim())
      .filter(Boolean) ?? [];

  const technicalEmails = await Promise.all(
    technicalIds.map((id) => getEmail(id)),
  );
  const salesEmails = await Promise.all(salesIds.map((id) => getEmail(id)));

  // Customer contacts are captured per role (sales / technical / organizational)
  // as CustomerContact complex objects ({ email, firstName, ... }), plus a
  // legacy single field that stored comma-joined ids/e-mails. Aggregate them all
  // into one list, de-duplicated, so downstream consumers (table, /contacts,
  // docs) keep working.
  const customerIds = Array.from(
    new Set(
      [
        "customerContact",
        "customerSalesContact",
        "customerTechnicalContact",
        "customerOrganizationalContact",
      ].flatMap((key) => {
        const raw = customerRaw[key];
        if (!raw) return [];
        // CustomerContact complex object -> use its e-mail
        if (typeof raw === "object") {
          return raw.email ? [String(raw.email).trim()] : [];
        }
        // legacy: comma-joined ids / e-mails
        return String(raw)
          .split(",")
          .map((id: string) => id.trim())
          .filter(Boolean);
      }),
    ),
  );

  const customerEmails = await Promise.all(
    customerIds.map((id) => (!isValidEmail(id) ? getEmail(id) : id)),
  );

  // Dedicated update-notification recipients. The combo-box editor stores a
  // comma-joined list of Keycloak user ids and/or free-typed e-mails; resolve
  // ids to e-mails and keep already-valid e-mails as-is. These are NOT
  // provisioned as Keycloak users (see catalog-action/contacts.ts).
  const updateNotificationIds =
    customerRaw["updateNotificationContacts"]
      ?.split(",")
      .map((id: string) => id.trim())
      .filter(Boolean) ?? [];
  const updateNotificationEmails = await Promise.all(
    updateNotificationIds.map((id: string) =>
      !isValidEmail(id) ? getEmail(id) : id,
    ),
  );

  const filteredEmails = technicalEmails.filter(Boolean);
  const filteredSales = salesEmails.filter(Boolean);
  const filteredCustomers = customerEmails.filter(Boolean);
  const filteredUpdateNotifications = Array.from(
    new Set(updateNotificationEmails.filter(Boolean)),
  );

  const contacts: OpsanioGeneralContacts = {
    technical: filteredEmails,
    sales: filteredSales,
    customer: filteredCustomers,
    updateNotifications: filteredUpdateNotifications,
  };
  return {
    contacts,
    dates: {
      end: parseDate(endDate),
      start: parseDate(startDate),
    },
    poc,
  };
};

export const getAssemblySite = async (
  obj: Record<string, any>,
  getEmail?: (id: string) => Promise<string>,
): Promise<OpsanioAssemblySite> => {
  const values = getItemsByKeyPrefix(obj, "cpe.");
  const counts: Record<string, any> = getItemsByKeyPrefix(
    obj,
    "networks.exanio_cloud.",
  );
  const vpc = getItemsByKeyPrefix(obj, "networks.vpc.");
  const general = await getOpsanioContact(obj, getEmail);
  const vpcName =
    vpc.vpcName !== null && vpc.vpcName !== undefined ? vpc.vpcName : "";
  const emptyLocation = {
    displayName: "",
    lat: 0,
    lng: 0,
  };
  // cpe.location persists as a JSON-stringified array of sites. Downstream
  // (helm values, mail, netbox, ...) consumes a single site, so resolve the
  // one flagged as primary (Hauptstandort), falling back to the first entry.
  let location = emptyLocation;
  if (values.location && typeof values.location === "string") {
    const parsedLocations = JSON.parse(values.location);
    if (Array.isArray(parsedLocations)) {
      location =
        parsedLocations.find((l: any) => l?.isPrimary) ??
        parsedLocations[0] ??
        emptyLocation;
    } else if (parsedLocations) {
      location = parsedLocations;
    }
  }
  if (location.displayName.trim() === "") {
    const street = values["addrone.street"];
    const city = values["addrtwo.city"];
    if (typeof street === "string" && typeof city === "string") {
      location = {
        ...location,
        displayName: `${street}, ${city}`,
      };
    }
  }

  // `number_of_agents` counts the node slots ever allocated, not the nodes that
  // exist: a decommissioned node keeps its slot so the surviving nodes are never
  // renumbered (see OpsanioAppliance.removed). Charts size their workloads off
  // the real node count, so report the active rows here and only fall back to
  // the raw answer while no agent rows have been generated yet.
  const agentRows = getAgents(obj);
  const agentSlots = parseFloat(counts.numberOfAgents);
  const agentCounts = {
    agents:
      agentRows.length > 0
        ? agentRows.filter((agent) => !agent.removed).length
        : agentSlots,
    agentSlots: Number.isNaN(agentSlots) ? agentRows.length : agentSlots,
  };

  const parsed: OpsanioAssemblySite = {
    location,
    contact: values.email,
    rack: {
      airGaped: getYesNoAsBoolean(values["rack.airgap"]),
      huFree: getYesNoAsBoolean(values["rack.hu"]),
      twoRoom: getYesNoAsBoolean(values["tworoom.tworoomq"]),
    },
    counts: {
      ...agentCounts,
      server: parseFloat(counts.numberOfMaster),
    },
    switch: {
      portsFree: getYesNoAsBoolean(values["rack.swsport"]),
      useTwo: getYesNoAsBoolean(values["rack.twosws"]),
    },
    metadata: {
      name: vpcName,
      owner: vpcName.split("-")[0],
      project: vpcName.split("-")[1],
      stage: vpcName.split("-")[2],
    },
    general,
  };
  return parsed;
};

const getCommonPrefix = (strings: string[]): string => {
  if (strings.length === 0) return "";
  let prefix = strings[0];
  for (let i = 1; i < strings.length; i++) {
    while (!strings[i].startsWith(prefix) && prefix.length > 0) {
      prefix = prefix.slice(0, -1);
    }
  }
  return prefix;
};

export const groupByPrefix = (
  obj: Record<string, any>,
): Record<string, Record<string, any>> => {
  const groupedKeys: Record<string, string[]> = {};
  const unsortedResult: Record<string, Record<string, any>> = {};

  for (const key of Object.keys(obj)) {
    const [prefix, fullKey] = key.split(".", 2);
    if (!prefix || !fullKey) continue;
    if (!groupedKeys[prefix]) groupedKeys[prefix] = [];
    groupedKeys[prefix].push(fullKey);
  }

  const cleanerPrefixes: Record<string, string> = {};
  for (const [prefix, keys] of Object.entries(groupedKeys)) {
    cleanerPrefixes[prefix] = getCommonPrefix(keys);
  }

  for (const [key, value] of Object.entries(obj)) {
    const [prefix, fullKey] = key.split(".", 2);
    if (!prefix || !fullKey) continue;

    const cleanerPrefix = cleanerPrefixes[prefix] || "";
    const strippedKey = fullKey.slice(cleanerPrefix.length);
    if (!strippedKey) continue;

    const finalKey = strippedKey.charAt(0).toLowerCase() + strippedKey.slice(1);

    if (!unsortedResult[prefix]) unsortedResult[prefix] = {};

    let val = value;
    if (finalKey === "size") {
      if (typeof value === "string") {
        const parsed = parseInt(value, 10);
        val = Number.isFinite(parsed) ? gibToBytes(parsed) : "";
      } else if (value === -1) {
        val = value;
      } else if (typeof value === "number") {
        val = gibToBytes(value);
      } else {
        val = "";
      }
    }

    unsortedResult[prefix][finalKey] = val;
  }

  const sortedEntries = Object.entries(unsortedResult).sort(([, a], [, b]) => {
    const sizeA = typeof a.size === "number" ? a.size : -Infinity;
    const sizeB = typeof b.size === "number" ? b.size : -Infinity;

    if (sizeA !== sizeB) return sizeB - sizeA;

    const mountA = typeof a.mount === "string" ? a.mount : "";
    const mountB = typeof b.mount === "string" ? b.mount : "";
    return mountA.localeCompare(mountB);
  });

  const result: Record<string, Record<string, any>> = {};
  for (const [prefix, data] of sortedEntries) {
    result[prefix] = data;
  }

  return result;
};

/**
 * Fallback disk list emitted when neither NetBox storage nor the legacy
 * `hardware.device_conf.ssd_*` answers yield any devices. An empty `devices: []`
 * breaks downstream provisioning, so we default to 7 disks (sda..sdg) - the
 * standard appliance layout.
 */
export const DEFAULT_DISK_DEVICES: string[] = Array.from(
  { length: 7 },
  (_, i) => `sd${getLetterFromNumber(i + 1)}`,
);

export const getStorage = (
  obj: Record<string, any>,
  hardware?: OpsanioHardware,
): OpsanioStorageConf => {
  const encryption: OpsanioEncryption = {
    cipher: "aes-xts-plain64",
    key_size: 512,
    //key: "NHV0MG51YjFsKzBwczRuaTA=",
    key: "",
  };

  const data = getItemsByKeyPrefix(obj, "hardware.data_vol.sys_vol_");
  const dataVolume = groupByPrefix(data);

  const sysVol = getItemsByKeyPrefix(obj, "hardware.sys_vol.sys_vol_");
  const systemVolume = groupByPrefix(sysVol);
  const totalSystemSize = Object.values(systemVolume).reduce(
    (sum, partition) => sum + (partition.size ?? 0),
    0,
  );
  const vg = getItemsByKeyPrefix(obj, "hardware.vg.vg");

  if (hardware) {
    let hardDisk = hardware.storage.disks.map((disk, idx) => {
      if (disk.type === "NVMe") {
        return `nvme${idx}n1`;
      }
      return `sd${getLetterFromNumber(idx + 1)}`;
    });
    if (hardDisk.length === 0) {
      const oldId = Object.values(
        getItemsByKeyPrefix(obj, "hardware.device_conf.ssd_"),
      );
      hardDisk = oldId.length !== 0 ? oldId : [...DEFAULT_DISK_DEVICES];
    }

    const primaryDisk = hardDisk[0] ?? "sda";

    const nbStorage = {
      raid: {
        system_size: totalSystemSize,
      },
      disks: {
        bootSize: 1073741824,
        devices: hardDisk,
        efiSize: 536870912,
        primaryDisk,
      },
      dataVolume,
      systemVolume,
      encryption,
      volumeGroups: vg,
    };
    return nbStorage;
  } else {
    const values = getItemsByKeyPrefix(obj, "hardware.");

    const ssdDevices = Object.values(
      getItemsByKeyPrefix(obj, "hardware.device_conf.ssd_"),
    );
    const devices =
      ssdDevices.length !== 0 ? ssdDevices : [...DEFAULT_DISK_DEVICES];

    const storage: OpsanioStorageConf = {
      raid: {
        system_size: totalSystemSize,
      },
      disks: {
        bootSize: 1073741824,
        devices,
        efiSize: 536870912,
        primaryDisk: values["deviceConf.primaryDisk"],
      },
      dataVolume,
      systemVolume,
      volumeGroups: vg,
      encryption,
    };

    return storage;
  }
};

export const getHardware = async (
  obj: Record<string, any>,
  fetchDeviceFunction?: (id: string) => Promise<any>,
): Promise<OpsanioHardware> => {
  const netboxIDs: string[] = (
    getItemsByKeyPrefix(obj, "hardware.")["servers"]?.split(",") ?? []
  )
    .map((id: string) => id.trim())
    .filter(Boolean);

  const devices: Array<OpsanioHardwareDevice> = [];
  let hardware: OpsanioHardware = {
    cpu: [],
    memory: [],
    nic: [],
    storage: {
      size: 0,
      type: "Unknown",
      disks: [],
    },
    devices,
  };

  if (!fetchDeviceFunction) {
    return hardware;
  }

  // The caller hands over `fetchDeviceById`, which answers with an
  // AnketaResponse envelope - reading `.inventory` off that yields undefined,
  // which is why CPU / RAM / disks came back empty for every appliance. Accept
  // both shapes so either kind of fetcher works.
  const unwrapDevice = (result: any): any =>
    result &&
    typeof result === "object" &&
    "data" in result &&
    "success" in result
      ? result.data
      : result;

  const netboxDevices = (
    await Promise.all(netboxIDs.map((id) => fetchDeviceFunction(id)))
  )
    .map(unwrapDevice)
    .filter(Boolean);

  const STORAGE_MEDIA_MAP: Record<string, OpsanioHardwareStorageMediaType> = {
    ssd: "SSD",
    hdd: "HDD",
    nvme: "NVMe",
  };

  const NIC_SPEED_MAP: Record<string, number> = {
    "10gbase-t": 10000,
    "10gbase-x-sfpp": 10000,
    "10gbase-x-xfp": 10000,
    "10gbase-x-sfpplus": 10000,
    "10gbase-cx4": 10000,
    "10gbase-kr": 10000,
    "1000base-t": 1000,
    "1000base-x-gbic": 1000,
    "1000base-x-sfp": 1000,
    "100base-tx": 100,
    "25gbase-x-sfp28": 25000,
    "40gbase-x-qsfpp": 40000,
    "100gbase-x-qsfp28": 100000,
  };

  for (const d of netboxDevices) {
    const cpu: Array<OpsanioHardwareCpu> = (d?.inventory ?? [])
      .filter(
        (item) =>
          !(item.name ?? "").toUpperCase().includes("DIMM") &&
          ((item.name ?? "").match(/^proc\s*\d+\s*-/i) ||
            (item.description ?? "").toLowerCase().includes("cores")),
      )
      .map((item) => ({
        model:
          (item.name ?? "").match(/^Proc\s*\d+\s*-\s*(.+)$/i)?.[1] ?? item.name,
        cores: parseInt(
          (item.description ?? "").match(/(\d+)\s*Cores/i)?.[1] ?? "0",
          10,
        ),
        threads: parseInt(
          (item.description ?? "").match(/(\d+)\s*Threads/i)?.[1] ?? "0",
          10,
        ),
        ghz: parseFloat(
          (item.name ?? "").match(/@\s*([\d.]+)\s*GHz/i)?.[1] ?? "0",
        ),
        serial: item.serial ?? "",
      }));

    const memory: Array<OpsanioHardwareRam> = (d?.inventory ?? [])
      .filter(
        (item) =>
          (item.name ?? "").toUpperCase().includes("DIMM") ||
          (!(item.name ?? "").match(/^proc\s*\d+\s*-/i) &&
            (item.description ?? "").toUpperCase().includes("DDR")),
      )
      .filter((item) => (item.name ?? "").match(/(\d+)GB/i))
      .map((item) => ({
        proc: parseInt(
          (item.name ?? "").match(/^PROC\s*(\d+)/i)?.[1] ?? "0",
          10,
        ),
        dimm: parseInt(
          (item.name ?? "").match(/DIMM\s*(\d+)/i)?.[1] ?? "0",
          10,
        ),
        size: parseInt((item.name ?? "").match(/(\d+)\s*GB/i)?.[1] ?? "0", 10),
        speed: parseInt(
          (item.description ?? "").match(/(\d+)\s*MHz/i)?.[1] ?? "0",
          10,
        ),
        type: (item.description ?? "").match(/(DDR\d?)/i)?.[1] ?? "",
        part: item.part_id ?? "",
      }));

    const nic: Array<OpsanioHardwareNic> = (d?.interfaces ?? [])
      .filter(
        (iface) =>
          !iface.mgmt_only && iface.mac_address !== null && iface.enabled,
      )
      .map((iface) => ({
        speed: NIC_SPEED_MAP[iface.type?.value] ?? 0,
        mac: iface.mac_address ?? "",
      }));

    // Drives tagged `defect` in NetBox are dropped here rather than anywhere
    // downstream, so every consumer - the disk layout, the documentation, the
    // capacity summary - agrees on the disks the machine actually has. Tagging
    // the inventory item is how a failed drive is recorded; the device itself
    // stays in service.
    //
    // tags is optional in practice (older fixtures and hand-made items omit
    // it), so the check has to tolerate undefined.
    const disks: Array<OpsanioHardwareStorage> = (d?.inventory ?? [])
      .filter((item) => (item.name ?? "").match(/^Drive\s*\d+/i))
      .filter(
        (item) =>
          !(item.tags ?? []).some(
            (t) => t?.slug === OPSANIO_HARDWARE_DEFECT_TAG,
          ),
      )
      .map((item) => {
        const sizeMatch = (item.name ?? "").match(/(\d+(?:\.\d+)?)(GB|TB)/i);
        const rawSize = parseFloat(sizeMatch?.[1] ?? "0");
        const rawUnit = (sizeMatch?.[2] ?? "GB").toUpperCase();
        const mediaMatch = (item.name ?? "").match(
          /\d+(?:\.\d+)?(?:GB|TB)\s+(\w+)/i,
        );

        const sizeInBytes: number =
          rawUnit === "TB"
            ? Math.round(rawSize * 1024 * 1024 * 1024 * 1024)
            : Math.round(rawSize * 1024 * 1024 * 1024);

        // opsanio-logica packs everything that has no NetBox field of its own
        // into the description, pipe-separated:
        //   Bay: 2I:1:5 | Model: … | Serial: … | Capacity: 480GB |
        //   Media: SATA SSD | Health: OK | Life: 99% | Power-on: 1234h |
        //   Temp: 35C | Firmware: HPG4 | FAILURE PREDICTED |
        //   Status: StorageDevice.1.1.DriveFailure | Controller: …
        // Each field is read on its own and absent ones stay empty, so a
        // description shortened by NetBox's 200-character limit costs only the
        // fields that were cut off.
        const description = item.description ?? "";
        const field = (label: string): string =>
          description
            .match(new RegExp(`${label}:\\s*([^|]+)`, "i"))?.[1]
            ?.trim() ?? "";
        /** A reported number, or null - never 0, which would read as a value. */
        const numeric = (raw: string): number | null => {
          if (raw === "") return null;
          const value = parseFloat(raw);
          return Number.isFinite(value) ? value : null;
        };

        return {
          position: parseInt(
            (item.name ?? "").match(/^Drive\s*(\d+)/i)?.[1] ?? "0",
            10,
          ),
          size: sizeInBytes,
          type:
            STORAGE_MEDIA_MAP[mediaMatch?.[1]?.toLowerCase() ?? ""] ??
            "Unknown",
          controller: field("Controller"),
          health: field("Health"),
          label: item.label ?? "",
          // Identity comes from the item's own fields, not the description:
          // logica writes the model to part_id and the serial to serial, and
          // only when the iLO actually reported them.
          partNumber: item.part_id ?? "",
          serial: item.serial ?? "",
          firmware: field("Firmware"),
          lifeLeftPct: numeric(field("Life").replace("%", "")),
          powerOnHours: numeric(field("Power-on").replace(/h$/i, "")),
          temperatureC: numeric(field("Temp").replace(/C$/i, "")),
          // A standalone marker, not a "label: value" pair.
          failurePredicted: /FAILURE PREDICTED/i.test(description),
          statusReason: field("Status"),
        };
      })
      .sort((a, b) => a.position - b.position);

    let storageSummarySize: number = 0;
    let storageSummaryType: OpsanioHardwareStorageMediaType = "Unknown";

    if (disks.length > 0) {
      storageSummarySize = disks.reduce((sum, disk) => sum + disk.size, 0);

      const typeCounts = disks.reduce<Record<string, number>>((acc, disk) => {
        acc[disk.type] = (acc[disk.type] ?? 0) + 1;
        return acc;
      }, {});
      storageSummaryType = (Object.entries(typeCounts).sort(
        (a, b) => b[1] - a[1],
      )[0]?.[0] ?? "Unknown") as OpsanioHardwareStorageMediaType;
    } else {
      const cfRaw = d?.custom_fields?.["storage"];

      if (cfRaw !== null && cfRaw !== undefined && cfRaw !== "") {
        const cfStr = String(cfRaw).trim();
        const cfMatch = cfStr.match(/^([\d.]+)\s*(GB|TB)?$/i);
        const cfSize = parseFloat(cfMatch?.[1] ?? cfStr);
        const cfUnit = (cfMatch?.[2] ?? "GB").toUpperCase();

        if (!isNaN(cfSize)) {
          storageSummarySize =
            cfUnit === "TB"
              ? Math.round(cfSize * 1024 * 1024 * 1024 * 1024)
              : Math.round(cfSize * 1024 * 1024 * 1024);
        }
      }
    }

    const storage = {
      size: storageSummarySize,
      type: storageSummaryType,
      disks,
    };

    devices.push({
      id: String(d?.id ?? ""),
      name: d?.name ?? "",
      serialNumber: d?.serial ?? "",
      cpu,
      memory,
      nic,
      storage,
    });

    // The flat fields describe a single server. They are kept as-is (last
    // device wins) because helm values, the storage layout and the dashboard
    // read them; `devices` is what carries the full picture.
    hardware = { cpu, memory, nic, storage, devices };
  }

  return hardware;
};

export const getTransferNetwork = (
  obj: Record<string, any>,
): OpsanioNetwork => {
  if (!obj || typeof obj !== "object") {
    console.warn("getAgents: obj is null, undefined, or not an object", {
      obj,
    });
    // vlan 0 is reserved by 802.1Q, so it is the unambiguous "unset" marker.
    // Do NOT use 1 here - that is the usable native VLAN and would read as real
    // data to isValidVlan.
    return {
      name: "Transfer",
      cidr: "",
      vlan: 0,
      prefix: 0,
    };
  }

  const values = getItemsByKeyPrefix(obj, "networks.transfer.");
  const parsed: OpsanioNetwork = {
    name: "Transfer",
    cidr: values.transferNetwork,
    vlan: parseFloat(values.transferVlan),
    prefix: parseFloat(values.transferNetwork?.split("/")[1]),
  };
  return parsed;
};

export const getWireGuardEndpoint = (
  obj: Record<string, any>,
): OpsanioWireGuard => {
  if (!obj || typeof obj !== "object") {
    console.warn("getAgents: obj is null, undefined, or not an object", {
      obj,
    });
    return {
      ip: "",
      dns: "",
      interface: "",
      port: 0,
    };
  }

  const values = getItemsByKeyPrefix(obj, "networks.wireguard.");
  values.interface = "wg0";
  const parsed: OpsanioWireGuard = {
    dns: values.wireguardDns,
    interface: "wg0",
    port: parseFloat(values.wireguardPort),
    ip: values.wireguardIp,
  };
  return parsed;
};

export const getCpeNetwork = (obj: Record<string, any>): OpsanioNetwork => {
  if (!obj || typeof obj !== "object") {
    console.warn("getAgents: obj is null, undefined, or not an object", {
      obj,
    });
    // vlan 0 = unset (reserved id); 1 is a real, usable VLAN. See getTransferNetwork.
    return {
      name: "",
      cidr: "",
      vlan: 0,
    };
  }

  const network = getItemsByKeyPrefix(obj, "networks.cpe_network.");
  const shared = getItemsByKeyPrefix(obj, "networks.agents.cpe_share.");

  const twoRoomSite =
    getYesNoAsBoolean(getItemsByKeyPrefix(obj, "cpe.")["tworoom.tworoomq"]) ===
    true;
  const roomSplit =
    twoRoomSite &&
    getYesNoAsBoolean(
      getItemsByKeyPrefix(obj, "networks.customer.").customerZoned,
    ) === true;

  // True only when EVERY appliance's exanio address falls inside the half its
  // room owns. Anything else - one stray node, a half-migrated site, the legacy
  // even/odd allocation - and consumers must keep using the parent prefix,
  // because a mix of /26 and /27 across peers is asymmetric routing: one side
  // thinks the other is on-link and the other does not.
  const inCidr = (ip: string, cidr: string): boolean => {
    if (!ip || !cidr || !cidr.includes("/")) return false;
    const [net, bitsRaw] = cidr.split("/");
    const bits = parseInt(bitsRaw, 10);
    const toInt = (a: string) =>
      a
        .split(".")
        .reduce((acc, o) => (acc << 8) + (parseInt(o, 10) || 0), 0) >>> 0;
    if (!Number.isFinite(bits) || bits < 0 || bits > 32) return false;
    const mask = bits === 0 ? 0 : (0xffffffff << (32 - bits)) >>> 0;
    return (toInt(ip) & mask) === (toInt(net) & mask);
  };
  const halfFor = (zone: string) =>
    zone === "B" ? network.cpeNetworkNetworkB : network.cpeNetworkNetworkA;
  let roomAligned = false;
  if (roomSplit) {
    const agents = getAgents(obj).filter((a) => !a.removed);
    roomAligned =
      agents.length > 0 &&
      agents.every((a) => inCidr(a.ips?.cpe ?? "", halfFor(a.zone)));
  }

  const parsed: OpsanioCpeNetwork = {
    name: "CPE",
    cidr: network.cpeNetworkNetwork,
    isVm: getYesNoAsBoolean(network.isvm),
    useUefi: getYesNoAsBoolean(network.useuefi),
    vlan: parseFloat(network.cpeNetworkVlan),
    shared: {
      ip: shared.agentCpeSharedIp,
      mac: shared.agentCpeSharedMac,
    },
    prefix: parseFloat(network.cpeNetworkNetwork?.split("/")[1]),
    // The halves are tagged with a room, and whether that tagging is HONEST is
    // stated separately as isRoomAligned.
    //
    // Historically the halves were handed out by agent index parity - even
    // agents got CPE A, odd got CPE B - which interleaves them across rooms.
    // On a two-room site that is the worst possible arrangement: the two
    // cross-room pairs end up inside the SAME /27 (maximally on-link across an
    // L2 that does not span the rooms), while the two same-room peers land in
    // DIFFERENT /27s. Addressing a node with its half's /27 under that layout
    // breaks the pair that currently works.
    //
    // So the prefix a node should use cannot be decided from its own address
    // alone - it depends on whether EVERY appliance sits in its own room's
    // half. That is a whole-site property, and it needs CIDR arithmetic, so it
    // is computed here rather than left to a template that can do neither.
    isTwoRoom: twoRoomSite,
    isDifferentNetwork: roomSplit,
    isRoomAligned: roomAligned,
    subnets: [
      {
        name: "CPE A",
        ...(roomSplit ? { zone: "A" as const } : {}),
        cidr: network.cpeNetworkNetworkA,
        vlan: parseFloat(network.cpeNetworkVlan),
        prefix: parseFloat(network.cpeNetworkNetworkA?.split("/")[1]),
      },
      {
        name: "CPE B",
        ...(roomSplit ? { zone: "B" as const } : {}),
        cidr: network.cpeNetworkNetworkB,
        vlan: parseFloat(network.cpeNetworkVlan),
        prefix: parseFloat(network.cpeNetworkNetworkB?.split("/")[1]),
      },
    ],
  };

  return parsed;
};

export const getCustomerNetwork = (
  obj: Record<string, any>,
): OpsanioNetwork => {
  if (!obj || typeof obj !== "object") {
    console.warn("getAgents: obj is null, undefined, or not an object", {
      obj,
    });
    // vlan 0 = unset (reserved id); 1 is a real, usable VLAN. See getTransferNetwork.
    return {
      name: "",
      cidr: "",
      vlan: 0,
    };
  }

  const network = getItemsByKeyPrefix(obj, "networks.customer.");
  const shared = getItemsByKeyPrefix(obj, "networks.agents.customer_share.");
  network.networkPrefix = calculateHostIP(network.customerCidr, 1);

  // customer_dns_entries persists as a JSON-stringified array of static DNS
  // records ({ name, type, ip }); normalise it back into an array so it is
  // emitted for both /api/v2/questionnaires/most and the S3 endpoint.
  const dnsEntries: OpsanioDnsEntry[] = parseDnsEntries(
    network.customerDnsEntries,
  );

  // Two-room sites split the customer network: each room has its own customer
  // CIDR and VLAN, and an appliance is reachable only on the one it is cabled
  // to.
  //
  // TWO gates, and both have to be true:
  //
  //   cpe.tworoom.tworoomq  - "Do you want a two-room solution?", the existing
  //     question in the LOCATION catalog (parsed into
  //     OpsanioAssemblySite.rack.twoRoom). This is the site fact.
  //
  //   networks.customer.customer_zoned - whether the two rooms actually have
  //     different customer networks. They often do not: two rooms on one
  //     stretched L2 share a CIDR and a VLAN, and only the physical placement
  //     differs. Splitting the network in that case would invent a second
  //     subnet nobody asked for.
  //
  // So a two-room site with one customer network still assigns nodes to rooms
  // (that is the site fact, and it drives the room question on each agent) but
  // emits a single subnet.
  //
  // `subnets` is emitted ALWAYS, with zone A mirroring the top-level values,
  // not only when the site is zoned. A consumer can then resolve an appliance
  // by its zone unconditionally instead of testing the flag first and falling
  // back - and an appliance always has a zone, "A" by default. The top-level
  // cidr/vlan stay exactly as they were, so nothing that ignores subnets sees
  // any change.
  //
  // Zone B is dropped when the flag is off or its CIDR is blank: a half-filled
  // second room is a questionnaire in progress, and emitting a subnet with an
  // empty CIDR would hand appliances an address of "" rather than an error.
  const twoRoomSite =
    getYesNoAsBoolean(getItemsByKeyPrefix(obj, "cpe.")["tworoom.tworoomq"]) ===
    true;
  const zoneB =
    twoRoomSite &&
    getYesNoAsBoolean(network.customerZoned) === true &&
    !!network.customerCidrB?.trim();

  const subnets: OpsanioNetwork[] = [
    {
      name: "Customer A",
      zone: "A",
      cidr: network.customerCidr,
      vlan: parseFloat(network.customerVlan),
      gw: network.customerGw,
      dns: network.customerDns,
      ntp: network.customerNtp,
      prefix: parseFloat(network.customerCidr?.split("/")[1]),
    },
  ];
  if (zoneB) {
    subnets.push({
      name: "Customer B",
      zone: "B",
      cidr: network.customerCidrB,
      // Everything except the CIDR falls back to room A when left blank.
      //
      // The VLAN especially: blank means "same id as A" rather than 0, because
      // one L2 stretched across both rooms with two subnets on it is a real
      // topology - and 0 is a reserved id that would render an unusable
      // interface. Same reasoning for the gateway, resolver and time source: a
      // site that only differs in placement overrides nothing, while rooms in
      // separate fire compartments usually carry their own local resolver and
      // time source and fill these in.
      vlan: parseFloat(network.customerVlanB || network.customerVlan),
      gw: network.customerGwB || network.customerGw,
      dns: network.customerDnsB || network.customerDns,
      ntp: network.customerNtpB || network.customerNtp,
      prefix: parseFloat(network.customerCidrB?.split("/")[1]),
    });
  }

  const parsed: OpsanioNetwork = {
    name: "Customer",
    // The two facts a consumer has to branch on, stated rather than inferred.
    //
    // Counting subnets would usually give the same answer, but not always, and
    // the difference matters: a site can be two-room with ONE customer network,
    // where the rooms differ physically and the nodes are still on the same L2.
    // A consumer that guesses from the subnet count cannot tell that apart from
    // a single-room site, and the two want opposite behaviour - host routes via
    // the room gateway in one case, plain on-link in the other.
    isTwoRoom: twoRoomSite,
    isDifferentNetwork: zoneB,
    cidr: network.customerCidr,
    vlan: parseFloat(network.customerVlan),
    dns: network.customerDns,
    gw: network.customerGw,
    ntp: network.customerNtp,
    prefix: parseFloat(network.customerCidr?.split("/")[1]),
    shared: {
      ip: shared.agentCustomerSharedIp,
      mac: shared.agentCustomerSharedMac,
    },
    subnets,
    ...(dnsEntries.length > 0 ? { dnsEntries } : {}),
  };
  return parsed;
};

/**
 * Normalise the raw customer_dns_entries fact into a clean OpsanioDnsEntry[].
 * The fact is stored by the DNS-entries editor as a JSON string, but may also
 * already be an array (older/normalised facts); only entries with at least one
 * non-empty field are kept and each field is coerced to a trimmed string.
 */
const parseDnsEntries = (raw: unknown): OpsanioDnsEntry[] => {
  let list: unknown = raw;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (trimmed === "") return [];
    try {
      list = JSON.parse(trimmed);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(list)) return [];
  return list
    .map((entry) => ({
      name: String((entry as any)?.name ?? "").trim(),
      type: String((entry as any)?.type ?? "").trim(),
      ip: String((entry as any)?.ip ?? "").trim(),
    }))
    .filter(
      (entry) => entry.name !== "" || entry.type !== "" || entry.ip !== "",
    );
};

// The answer is a DNS domain that Traefik matches Host rules and header
// middlewares on, so everything around it has to go: the field used to be
// prefilled with a URL and answers in that shape are still stored, and a
// wildcard or a trailing dot would never match a router rule.
export const normalizeCustomerDomain = (value: unknown): string => {
  if (typeof value !== "string") {
    return "";
  }
  return value
    .trim()
    .toLowerCase()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//, "") // scheme
    .replace(/\/.*$/, "") // path
    .replace(/:\d+$/, "") // port
    .replace(/^\*\./, "") // wildcard label
    .replace(/\.+$/, ""); // root dot
};

export const getCustomerSettings = (
  obj: Record<string, any>,
): OpsanioCustomerSettings => {
  if (!obj || typeof obj !== "object") {
    console.warn("getAgents: obj is null, undefined, or not an object", {
      obj,
    });
    return {
      auth: {
        oidc: {
          useAutonubil: false,
          enabled: false,
          redirect: "",
          client: {
            id: "",
            secret: "",
          },
          scope: "",
          connect: "",
        },
        ldap: {
          enabled: false,
          ldaps: true,
          startTls: false,
          clientAuth: false,
          verifyHostnames: true,
          servers: [],
          bindUser: "opsanio.admin",
          userbase: "ou=people,dc=example,dc=com",
          usersearch: "(uid={0})",
          usernameAttribute: "uid",
          roleBase: "ou=groups,dc=example,dc=com",
          roleSearch: "(member={0})",
          userRoleAttribute: null,
          userRolename: "none",
          rolename: "cn",
          nestedRoles: true,
          customAttribute: [],
          domain: {
            name: "example.com",
            dc: ["example", "com"],
          },
        },
      },
      customerTld: "",
    };
  }

  // The customer TLD sits in its own row ("customer_settings"), not in the
  // customer NETWORK row ("customer"): the two share a prefix only up to
  // "networks.customer", so reading it with the network prefix - as this did -
  // silently produced an undefined value and dropped the answer.
  const dashboard = getItemsByKeyPrefix(obj, "networks.customer_settings.");
  // Gated on the same feature switch that hides the question, so an answer left
  // behind by a customer who was migrated back cannot quietly move every
  // ingress host onto a domain nobody asked for any more.
  const customDomainEnabled =
    getYesNoAsBoolean(getItemsByKeyPrefix(obj, "features.enable.").customDomain) ??
    false;

  const oidcRaw = getItemsByKeyPrefix(obj, "auth.oidc.");
  const ldapRaw = getKeyAsYesNoBoolean(getItemsByKeyPrefix(obj, "auth.ldap."), [
    "ldapEnable",
    "ldapsEnable",
    "ldapClientAuths",
    "ldapVerifyHostnames",
    "ldapResolveNestedRoles",
    "ldapStarttls",
  ]);
  const ldapPort: number = ldapRaw.ldapsEnable ? 636 : 389;
  const ldapServers: Array<string> = getArrayByKeyPrefix(ldapRaw, "ldapServer.")
    .filter((server) => server.trim() !== "")
    .map((server) => `${server}:${ldapPort}`);
  const customAttr: Array<string> = getArrayByKeyPrefix(
    ldapRaw,
    "ldapCustomAttr.",
  ).filter((attr) => attr.trim() !== "");
  const domain = ldapRaw.ldapDomain;

  const autonubilOidc = getYesNoAsBoolean(oidcRaw.useAutonubilOidc) ?? false;
  const genealUseOidc = getYesNoAsBoolean(oidcRaw.useOidc) ?? false;
  const oidc: OpsanioDashboardOidc = {
    useAutonubil: autonubilOidc,
    enabled: genealUseOidc || autonubilOidc,
    client: {
      id: oidcRaw.oidcClientId ?? "",
      secret: oidcRaw.oidcClientSecret ?? "",
    },
    redirect: oidcRaw.oidcRedirect ?? "",
    connect: oidcRaw.oidcClientConnect ?? "",
    scope: oidcRaw.oidcClientScope ?? "",
  };

  const ldap: OpsanioLdap = {
    enabled: ldapRaw.ldapEnable ?? false,
    ldaps: ldapRaw.ldapsEnable ?? false,
    startTls: ldapRaw.ldapStarttls ?? false,
    clientAuth: ldapRaw.ldapClientAuths ?? false,
    verifyHostnames: ldapRaw.ldapVerifyHostnames ?? false,
    servers: ldapServers ?? [],
    bindUser: ldapRaw.ldapDnUser ?? "",
    userbase: ldapRaw.ldapUsrbase ?? "",
    usersearch: ldapRaw.ldapUsrsearch ?? "",
    usernameAttribute: ldapRaw.ldapUsrAttr ?? "",
    roleBase: ldapRaw.ldapRolebase ?? "",
    roleSearch: ldapRaw.ldapRolesearch ?? "",
    userRoleAttribute:
      ldapRaw?.ldapUsrroleattr?.trim() !== "" ? ldapRaw.ldapUsrroleattr : null,
    userRolename:
      ldapRaw?.ldapUsrrolename?.trim() !== ""
        ? ldapRaw.ldapUsrrolename
        : "none",
    rolename: ldapRaw.ldapRolename ?? "",
    nestedRoles: ldapRaw.ldapResolveNestedRoles ?? false,
    customAttribute: customAttr ?? [],
    domain: {
      name: domain ?? "",
      dc: domain
        ? domain
            .split(".")
            .map((dc) => `dc=${dc}`)
            .join(",")
        : [],
    },
  };
  const customerSettings = {
    auth: {
      oidc,
      ldap,
    },
    // Empty is the normal case: the cluster stays on its `<vpc>.exanio.net`
    // domain and no customer certificate is installed.
    customerTld: customDomainEnabled
      ? normalizeCustomerDomain(dashboard.customerDnsName)
      : "",
  };

  return customerSettings;
};

export const baseMacs = ["06:99:C6:00:00:00", "02:99:C6:00:00:00"];

export const getAgents = (
  obj: Record<string, any>,
): Array<OpsanioAppliance> => {
  const agentGroups: Record<
    string,
    {
      exanioDescription: string;
      offset: string;
      ips: Record<string, any>;
      bgp: Record<string, any>;
    }
  > = {};

  if (!obj || typeof obj !== "object") {
    console.warn("getAgents: obj is null, undefined, or not an object", {
      obj,
    });
    return [];
  }
  const vpcName = getItemsByKeyPrefix(obj, "networks.vpc.vpc_name")[""];
  Object.entries(obj).forEach(([key, value]) => {
    const match = key.match(/^networks\.agents\.(agent_(\d+))\.\1_(.+)$/);
    if (match) {
      const idx = match[2];
      const property = match[3];
      const exanioDescription = `opsanio-00${idx}`;
      const hostname = `${vpcName}-a0-${exanioDescription}`;

      if (!agentGroups[hostname]) {
        agentGroups[hostname] = {
          exanioDescription,
          offset: idx,
          ips: {},
          bgp: {},
        };
      }
      agentGroups[hostname].ips[getLastKeyValue(property)] =
        extractValue(value);
    }

    const bgpMatch = key.match(
      /^networks\.agents\.(agent_(\d+))\.bgp_settings\.bgp_(.+)$/,
    );
    if (bgpMatch) {
      const idx = bgpMatch[2];
      const bgpProperty = bgpMatch[3];
      const exanioDescription = `opsanio-00${idx}`;
      const hostname = `${vpcName}-a0-${exanioDescription}`;
      if (!agentGroups[hostname]) {
        agentGroups[hostname] = {
          exanioDescription,
          offset: idx,
          ips: {},
          bgp: {},
        };
      }
      if (!(agentGroups[hostname] as any).bgp) {
        (agentGroups[hostname] as any).bgp = {};
      }
      (agentGroups[hostname] as any).bgp[bgpProperty] = extractValue(value);
    }
  });

  const features = getItemsByKeyPrefix(obj, "features.enable.");
  const tenantId = Number(getVpcValues(obj).vlan) || 0;

  return Object.entries(agentGroups).map(([hostname, data]) => {
    // Scoped to the tenant: without it every cluster mints the same addresses,
    // and most indexes every MAC in a document, so a by-MAC lookup can resolve
    // a node to another customer's cluster entirely.
    const macAddress = calculatePrivateMacAddresses(
      baseMacs,
      Number(data.offset),
      tenantId,
    );
    const cleanedIps = removeKeys(data.ips, [
      "macOne",
      "macTwo",
      "macRedfish",
      "removed",
      "zone",
    ]);
    const pipaCidr = "169.254.0.0/16";
    const agentBgpValues: OpsanioAgentBgp = {
      enabled: getYesNoAsBoolean(features.bgp) ?? false,
      description: data.bgp.description ?? "NOT CONFIGURED",
      cidr: data.bgp.cidr ?? pipaCidr,
      as: {
        local: data.bgp.local_as ? parseInt(data.bgp.local_as) : 123,
        neighbor: data.bgp.as ? parseInt(data.bgp.as) : 234,
      },
      ips: {
        local: data.ips.customer,
        neighbor: data.bgp.ip ?? calculateHostIP(pipaCidr, 1),
      },
      interfaces: ["vlan_customer"],
    };

    const parsed: OpsanioAppliance = {
      hostname,
      description: data.exanioDescription,
      offset: Number(data.offset),
      // yes_no: "0" is Yes ("this node was removed"). Unanswered rows - every
      // agent recorded before the flag existed - default to an active node.
      removed: getYesNoAsBoolean(data.ips.removed) ?? false,
      // Which room this appliance stands in, selecting its entry of
      // networks.customer.subnets. Anything other than an explicit "B" is "A":
      // every agent recorded before zones existed, every single-room site, and
      // any half-answered row. Defaulting the other way would move an existing
      // appliance onto a customer VLAN it is not cabled to.
      zone:
        String(data.ips.zone ?? "")
          .trim()
          .toUpperCase() === "B"
          ? "B"
          : "A",
      serialNumber: cleanedIps.sn,
      ips: {
        cpe: cleanedIps.cpe,
        customer: cleanedIps.customer,
        redfish: cleanedIps.redfish,
        transfer: cleanedIps.transfer,
      },
      macs: {
        cpe: macAddress[0],
        customer: macAddress[1],
        one: data.ips.macOne,
        two: data.ips.macTwo,
        redfish: data.ips.macRedfish,
      },
      bgp: agentBgpValues,
    };
    return parsed;
  });
};

/**
 * Parses the network switches into `server.switches`. The switches are not
 * attached to individual appliances; they are shared and come from the
 * `hardware.switches` NetBox selection, which stores a comma-separated list of
 * device ids and may therefore name several switches. When the customer
 * supplies the switch instead, the selection holds `CUSTOMER_SWITCH_VALUE` and
 * the details are typed into `hardware.switch.switch_row` - that yields the one
 * manual entry. Returns an array (empty if there is no switch data at all).
 */
export const getSwitches = (
  obj: Record<string, any>,
  netboxSwitches?:
    | string
    | NetBoxDeviceWithInventory
    | Array<NetBoxDeviceWithInventory | undefined | null>,
): Array<OpsanioSwitch> => {
  const switchValues = getItemsByKeyPrefix(obj, "hardware.switch.switch_row");
  const manualMac = switchValues[".switchMac"] || "";

  const selected: Array<NetBoxDeviceWithInventory> = (
    Array.isArray(netboxSwitches)
      ? netboxSwitches
      : [netboxSwitches as NetBoxDeviceWithInventory]
  ).filter(
    (device): device is NetBoxDeviceWithInventory =>
      Boolean(device) && typeof device !== "string",
  );

  if (selected.length > 0) {
    return selected.map((device) => ({
      serialNumber: device.serial ?? "",
      model: device.device_type?.model ?? "",
      mac:
        (device.interfaces?.find((inf) => typeof inf.mac_address === "string")
          ?.mac_address ??
          // Only a lone switch may borrow the manually typed MAC; with several
          // selected there is no telling which one it would belong to.
          (selected.length === 1 ? manualMac : "")) ||
        "",
    }));
  }

  const manual: OpsanioSwitch = {
    model: switchValues[".model"] || "",
    mac: manualMac,
    serialNumber: switchValues[".switchSn"] || "",
  };

  const hasData =
    manual.model !== "" || manual.mac !== "" || manual.serialNumber !== "";

  return hasData ? [manual] : [];
};

/**
 * Builds `server.dac` from the NetBox DAC-cable devices selected in the
 * questionnaire. The selected device ids (`hardware.dac`) are resolved to full
 * NetBox devices by the caller; here we map each to its serial/model.
 */
export const getDacs = (
  netboxDacs: Array<NetBoxDeviceWithInventory | undefined | null>,
): Array<OpsanioDac> =>
  (netboxDacs ?? [])
    .filter((d): d is NetBoxDeviceWithInventory => Boolean(d))
    .map((d) => ({
      serialNumber: d.serial ?? "",
      model: d.device_type?.model ?? "",
    }));

/**
 * Parses the customer-site probes from the flattened questionnaire content.
 * Probe answers are keyed as
 * `networks.probes.probe_<idx>.probe_<idx>_<field>` (mirroring the agents
 * convention). Returns one `OpsanioProbe` per probe, ordered by index.
 */
export const getProbes = (obj: Record<string, any>): Array<OpsanioProbe> => {
  const probeGroups: Record<string, Record<string, any>> = {};

  if (!obj || typeof obj !== "object") {
    console.warn("getProbes: obj is null, undefined, or not an object", {
      obj,
    });
    return [];
  }

  Object.entries(obj).forEach(([key, value]) => {
    const match = key.match(/^networks\.probes\.(probe_(\d+))\.\1_(.+)$/);
    if (match) {
      const idx = match[2];
      const property = match[3];
      if (!probeGroups[idx]) {
        probeGroups[idx] = {};
      }
      probeGroups[idx][getLastKeyValue(property)] = extractValue(value);
    }
  });

  return Object.entries(probeGroups)
    .map(([idx, data]) => {
      const storageCount = Number(data.storageCount);
      const parsed: OpsanioProbe = {
        index: Number(idx),
        locationName: data.locationName ?? "",
        serialNumber: data.sn ?? "",
        customer: {
          cidr: data.customerCidr ?? "",
          vlan: data.customerVlan ?? "",
          gateway: data.customerGw ?? "",
          dns: data.customerDns ?? "",
          ntp: data.customerNtp ?? "",
        },
        ips: {
          cpe: data.cpe ?? "",
          transfer: data.transfer ?? "",
          customer: data.customerIp ?? "",
        },
        macs: {
          one: data.macOne ?? "",
          two: data.macTwo ?? "",
        },
        storage: {
          count: Number.isFinite(storageCount) ? storageCount : 0,
          type: normalizeProbeStorageType(data.storageType),
        },
      };
      return parsed;
    })
    .sort((a, b) => a.index - b.index);
};

/**
 * Maps the probe storage-type option value ("ssd" / "nvme") to the shared
 * media-type union. Anything unrecognised falls back to "Unknown".
 */
const normalizeProbeStorageType = (
  raw: unknown,
): OpsanioHardwareStorageMediaType => {
  switch (String(raw ?? "").toLowerCase()) {
    case "ssd":
      return "SSD";
    case "nvme":
      return "NVMe";
    default:
      return "Unknown";
  }
};

export const getNetworkScanners = (
  obj: Record<string, any>,
): Array<OpsanioNetworkScanner> => {
  const scannerGroups: Record<string, Record<string, any>> = {};
  if (!obj || typeof obj !== "object") {
    console.warn("getAgents: obj is null, undefined, or not an object", {
      obj,
    });
    return [];
  }

  Object.entries(obj).forEach(([key, value]) => {
    const match = key.match(
      /^scanners\.network_scanner\.(network_scanner_(\d+))\.(.+)$/,
    );
    if (match) {
      const scannerId = match[1];
      let property = match[3];

      property = property.replace(/network_scanner_\d+_/g, "");
      property = getLastKeyValue(property);
      if (!scannerGroups[scannerId]) {
        scannerGroups[scannerId] = {};
      }
      scannerGroups[scannerId][property] = extractValue(value);
    }
  });
  const mapped = Object.values(scannerGroups).filter(
    (props) => props["name"] != null && props["cidr"] != null,
  );
  const updated: Array<OpsanioNetworkScanner> = mapped
    .map((value) => {
      return getKeyAsYesNoBoolean(value, [
        "fingerprint",
        "netbios",
        "snmp",
        "snmpV3",
      ]);
    })
    .map((scanner) => ({
      name: scanner.name,
      cidr: scanner.cidr,
      interval: scanner.interval,
      fingerprint: scanner.fingerprint,
      netbios: scanner.netbios,
      snmp: scanner.snmp,
      snmpV3: scanner.snmpV3,
      snmpSettingsUser: scanner.snmpSettingsUser,
      snmpSettingsPsw: scanner.snmpSettingsPsw,
      snmpSettingsStr: scanner.snmpSettingsStr,
    }));

  return updated;
};

export const getFortiGateScanners = (
  obj: Record<string, any>,
): Array<OpsanioForiGateScanner> => {
  const scannerGroups: Record<string, Record<string, any>> = {};
  if (!obj || typeof obj !== "object") {
    console.warn("getAgents: obj is null, undefined, or not an object", {
      obj,
    });
    return [];
  }

  Object.entries(obj).forEach(([key, value]) => {
    const match = key.match(
      /^scanners\.forti_scanner\.(forti_scanner_(\d+))\.(.+)$/,
    );
    if (match) {
      const scannerId = match[1];
      let property = match[3];

      property = property.replace(/forti_scanner_\d+_/g, "");

      if (!scannerGroups[scannerId]) {
        scannerGroups[scannerId] = {};
      }
      scannerGroups[scannerId][getLastKeyValue(property)] = extractValue(value);
    }
  });

  const mapped = Object.values(scannerGroups).filter(
    (props) => props["name"] != null,
  );

  const updated = mapped
    .map((value) => {
      return getKeyAsYesNoBoolean(value, ["insecure"]);
    })
    .map((scanner) => ({
      name: scanner.name,
      hostname: scanner.hostname,
      token: scanner.token,
      vdom: scanner.vdom,
      insecure: scanner.insecure,
    }));

  return updated;
};

export function getBaramundiScanners(
  obj: Record<string, any>,
): Array<OpsanioBaramundiScanner> {
  const scannerGroups: Record<string, Record<string, any>> = {};
  if (!obj || typeof obj !== "object") {
    console.warn("getAgents: obj is null, undefined, or not an object", {
      obj,
    });
    return [];
  }

  Object.entries(obj).forEach(([key, value]) => {
    const match = key.match(
      /^scanners\.baramundi_scanner\.(baramundi_scanner_(\d+))\.(.+)$/,
    );
    if (match) {
      const scannerId = match[1];
      let property = match[3];

      property = property.replace(/baramundi_scanner_\d+_/g, "");

      if (!scannerGroups[scannerId]) {
        scannerGroups[scannerId] = {};
      }
      scannerGroups[scannerId][getLastKeyValue(property)] = extractValue(value);
    }
  });

  const mapped = Object.values(scannerGroups).filter(
    (props) => props["name"] != null,
  );

  const updated = mapped
    .map((value) => {
      return getKeyAsYesNoBoolean(value, ["insecure"]);
    })
    .map((scanner) => ({
      name: scanner.name,
      url: scanner.url,
      username: scanner.username,
      password: scanner.password,
      insecure: scanner.insecure,
    }));

  return updated;
}

export function getIcingaTwoScanners(
  obj: Record<string, any>,
): Array<OpsanioIcingaTwoScanner> {
  const scannerGroups: Record<string, Record<string, any>> = {};
  if (!obj || typeof obj !== "object") {
    console.warn("getAgents: obj is null, undefined, or not an object", {
      obj,
    });
    return [];
  }

  Object.entries(obj).forEach(([key, value]) => {
    const match = key.match(
      /^scanners\.icinga_scanner\.(icinga_scanner_(\d+))\.(.+)$/,
    );
    if (match) {
      const scannerId = match[1];
      let property = match[3];

      property = property.replace(/icinga_scanner_\d+_/g, "");

      if (!scannerGroups[scannerId]) {
        scannerGroups[scannerId] = {};
      }
      scannerGroups[scannerId][getLastKeyValue(property)] = extractValue(value);
    }
  });

  const mapped = Object.values(scannerGroups).filter(
    (props) => props["name"] != null,
  );

  const updated = mapped
    .map((value) => {
      return getKeyAsYesNoBoolean(value, ["insecure"]);
    })
    .map((scanner) => ({
      name: scanner.name,
      url: scanner.url,
      insecure: scanner.insecure,
      password: scanner.password,
      webuiurl: scanner.webuiurl,
      username: scanner.username,
    }));

  return updated;
}

export function getIntuneScanners(
  obj: Record<string, any>,
): Array<OpsanioIntuneScanner> {
  const scannerGroups: Record<string, Record<string, any>> = {};
  if (!obj || typeof obj !== "object") {
    console.warn("getAgents: obj is null, undefined, or not an object", {
      obj,
    });
    return [];
  }

  Object.entries(obj).forEach(([key, value]) => {
    const match = key.match(
      /^scanners\.intune_scanner\.(intune_scanner_(\d+))\.(.+)$/,
    );
    if (match) {
      const scannerId = match[1];
      let property = match[3];

      property = property.replace(/intune_scanner_\d+_/g, "");

      if (!scannerGroups[scannerId]) {
        scannerGroups[scannerId] = {};
      }
      scannerGroups[scannerId][getLastKeyValue(property)] = extractValue(value);
    }
  });

  return Object.values(scannerGroups)
    .filter((props) => props["name"] != null)
    .map((scanner) => ({
      name: scanner.name,
      tenantId: scanner.tenantId,
      clientId: scanner.clientId,
      clientSecret: scanner.clientSecret,
    }));
}

export function getNetdataScanners(
  obj: Record<string, any>,
): Array<OpsanioNetdataScanner> {
  const scannerGroups: Record<string, Record<string, any>> = {};
  if (!obj || typeof obj !== "object") {
    console.warn("getAgents: obj is null, undefined, or not an object", {
      obj,
    });
    return [];
  }

  Object.entries(obj).forEach(([key, value]) => {
    const match = key.match(
      /^scanners\.netdata_scanner\.(netdata_scanner_(\d+))\.(.+)$/,
    );
    if (match) {
      const scannerId = match[1];
      let property = match[3];

      property = property.replace(/netdata_scanner_\d+_/g, "");

      if (!scannerGroups[scannerId]) {
        scannerGroups[scannerId] = {};
      }
      scannerGroups[scannerId][getLastKeyValue(property)] = extractValue(value);
    }
  });

  const mapped = Object.values(scannerGroups).filter(
    (props) => props["name"] != null,
  );

  const updated = mapped
    .map((value) => {
      return getKeyAsYesNoBoolean(value, ["insecure", "parallelism"]);
    })
    .map((scanner) => ({
      name: scanner.name,
      searchDomain: scanner.searchDomain,
      url: scanner.url,
      username: scanner.username,
      password: scanner.password,
      insecure: scanner.insecure,
      parallelism: scanner.parallelism,
    }));

  return updated;
}

export function getOctopusScanners(
  obj: Record<string, any>,
): Array<OpsanioOctopusScanner> {
  const scannerGroups: Record<string, Record<string, any>> = {};
  if (!obj || typeof obj !== "object") {
    console.warn("getAgents: obj is null, undefined, or not an object", {
      obj,
    });
    return [];
  }

  Object.entries(obj).forEach(([key, value]) => {
    const match = key.match(
      /^scanners\.octopus_scanner\.(octopus_scanner_(\d+))\.(.+)$/,
    );
    if (match) {
      const scannerId = match[1];
      let property = match[3];

      property = property.replace(/octopus_scanner_\d+_/g, "");

      if (!scannerGroups[scannerId]) {
        scannerGroups[scannerId] = {};
      }
      scannerGroups[scannerId][getLastKeyValue(property)] = extractValue(value);
    }
  });

  return Object.values(scannerGroups)
    .filter((props) => props["name"] != null)
    .map((scanner) => ({
      name: scanner.name,
      url: scanner.url,
      apiToken: scanner.apiToken,
      ocid: scanner.ocid,
      workspaceId: scanner.workspaceId,
    }));
}

export function getKubernetesServer(obj: Record<string, any>): OpsanioServers {
  const agentGroups: Record<string, Record<string, any>> = {};
  if (!obj || typeof obj !== "object") {
    console.warn("getAgents: obj is null, undefined, or not an object", {
      obj,
    });
    return { sharedIp: "169.254.0.1", servers: [] };
  }

  const vpcName = getItemsByKeyPrefix(obj, "networks.vpc.vpc_name")[""];
  const sharedIp = getItemsByKeyPrefix(
    obj,
    "networks.servers.server_shared_ip",
  )[""];

  Object.entries(obj).forEach(([key, value]) => {
    const match = key.match(/^networks\.servers\.(server_(\d+))\.\1_(.+)$/);

    if (match) {
      const idx = match[2];
      const property = match[3];
      const hostname = `${vpcName}-m0-k3server-00${idx}`;

      if (!agentGroups[hostname]) {
        agentGroups[hostname] = {};
      }
      agentGroups[hostname][getLastKeyValue(property)] = extractValue(value);
    }
  });

  const servers = Object.entries(agentGroups).map(([hostname, ips]) => ({
    hostname,
    ip: ips.ip,
  }));

  return {
    sharedIp,
    servers,
  };
}

export function getKula(obj: Record<string, any>): OpsanioKula {
  if (!obj || typeof obj !== "object") {
    console.warn("getAgents: obj is null, undefined, or not an object", {
      obj,
    });
    return {
      hostname: "missing-vpc-use-default",
      ips: {
        transfer: "169.254.0.1",
        host: "169.254.0.10",
      },
    };
  }

  const vpcName = getItemsByKeyPrefix(obj, "networks.vpc.vpc_name")[""];
  const ip = getItemsByKeyPrefix(obj, "networks.kula.kula.kula_ip")[""];
  const transfer = getItemsByKeyPrefix(
    obj,
    "networks.kula.kula.kula_ip_transfer",
  )[""];

  const hostname = `${vpcName}-m0-kula-001`;

  return {
    hostname,
    ips: {
      host: ip,
      transfer,
    },
  };
}

export function getProxmoxScanners(
  obj: Record<string, any>,
): Array<OpsanioProxmoxScanner> {
  const scannerGroups: Record<string, Record<string, any>> = {};
  if (!obj || typeof obj !== "object") {
    console.warn("getAgents: obj is null, undefined, or not an object", {
      obj,
    });
    return [];
  }

  Object.entries(obj).forEach(([key, value]) => {
    const match = key.match(
      /^scanners\.proxmox_scanner\.(proxmox_scanner_(\d+))\.(.+)$/,
    );
    if (match) {
      const scannerId = match[1];
      let property = match[3];

      property = property.replace(/proxmox_scanner_\d+_/g, "");

      if (!scannerGroups[scannerId]) {
        scannerGroups[scannerId] = {};
      }
      scannerGroups[scannerId][getLastKeyValue(property)] = extractValue(value);
    }
  });

  const mapped = Object.values(scannerGroups).filter(
    (props) => props["name"] != null,
  );

  const updated = mapped
    .map((value) => {
      return getKeyAsYesNoBoolean(value, ["insecure", "distrustOsData"]);
    })
    .map((scanner) => ({
      name: scanner.name,
      url: scanner.url,
      apiToken: scanner.apiToken,
      insecure: scanner.insecure,
      distrustOsData: scanner.distrustOsData,
    }));

  return updated;
}

export function getSentinalOneScanners(
  obj: Record<string, any>,
): Array<OpsanioSentinalOneScanner> {
  const scannerGroups: Record<string, Record<string, any>> = {};
  if (!obj || typeof obj !== "object") {
    console.warn("getAgents: obj is null, undefined, or not an object", {
      obj,
    });
    return [];
  }
  Object.entries(obj).forEach(([key, value]) => {
    const match = key.match(
      /^scanners\.sentinalOne_scanner\.(sone_scanner_(\d+))\.(.+)$/,
    );
    if (match) {
      const scannerId = match[1];
      let property = match[3];

      property = property.replace(/sone_scanner_\d+_/g, "");

      if (!scannerGroups[scannerId]) {
        scannerGroups[scannerId] = {};
      }
      scannerGroups[scannerId][getLastKeyValue(property)] = extractValue(value);
    }
  });

  const mapped = Object.values(scannerGroups).filter(
    (props) => props["name"] != null,
  );

  const updated = mapped
    .map((value) => {
      return getKeyAsYesNoBoolean(value, ["insecure", "distrustOsData"]);
    })
    .map((scanner) => ({
      name: scanner.name,
      insecure: scanner.insecure,
      proxy: scanner.proxy,
      apiKey: scanner.apiKey,
      customerDomain: scanner.customerDomain,
    }));

  return updated;
}

export const getOpsanioAgentValuesByOffset = (
  values: OpsanioValues,
  offset: number,
): OpsanioAgentValues | undefined => {
  const { networks, scanners, common, storage, wireguard } = values;
  const appliances = values.server.appliance;

  const appliance = appliances.find((a) => a.offset === offset);
  if (!appliance) return undefined;

  const other: OpsanioOtherAppliance[] = appliances
    .filter((a) => a.offset !== offset)
    .map((a) => ({ ip: a.ips?.cpe || "" }));

  const server: OpsanioAgentDevices = {
    appliance,
    kubernetes: values.server.kubernetes,
    kula: values.server.kula,
    other,
  };

  return {
    networks,
    scanners,
    server,
    common,
    storage,
    wireguard,
  };
};

export const getDnsHostsMapping = (
  vpc: string,
  agents: Array<OpsanioAppliance>,
  customerNetwork: OpsanioNetwork,
  transferNetwork: OpsanioNetwork,
): { hosts: Record<string, string[]>; external_ips: Array<string> } => {
  const hosts: Record<string, string[]> = {};
  const external_ips = [];
  agents.forEach((agent, idx) => {
    // `agent-<idx>` is pinned to the node's position in the agents list, so this
    // must be handed the *full* list, removed nodes included, and skip them in
    // place. Filtering them out beforehand would close the gap and repoint every
    // surviving node's DNS name at different hardware.
    if (agent.removed) return;
    const customer = `agent-${idx}.${vpc}.exanio.net`;
    const internal = `agent-${idx}-internal.${vpc}.exanio.net`;
    const transfer = `agent-${idx}-transfer.${vpc}.exanio.net`;

    external_ips.push(agent.ips.customer);
    hosts[agent.ips.customer] = [customer];
    hosts[agent.ips.cpe] = [internal];
    hosts[agent.ips.transfer] = [transfer];
  });
  if (customerNetwork.shared && customerNetwork.shared.ip) {
    hosts[customerNetwork.shared.ip] = [
      `opsanio.${vpc}.exanio.net`,
      `ipfix.${vpc}.exanio.net`,
      `netflow.${vpc}.exanio.net`,
      `syslog.${vpc}.exanio.net`,
      `snmp.${vpc}.exanio.net`,
      `wazuh.${vpc}.exanio.net`,
      `webhooks.${vpc}.exanio.net`,
      `ingest.${vpc}.exanio.net`,
      `dashboard.${vpc}.exanio.net`,
      `sentry.${vpc}.exanio.net`,
      `nessus.${vpc}.exanio.net`,
      `scanner.${vpc}.exanio.net`,
      `workflows.${vpc}.exanio.net`,
      `openvas.${vpc}.exanio.net`,
      `guacamole.${vpc}.exanio.net`,
      `dashboards.${vpc}.exanio.net`,
    ];
  }

  if (customerNetwork.gw) {
    hosts[customerNetwork.gw] = [`customer-gw.${vpc}.exanio.net`];
  }

  if (transferNetwork.gw) {
    hosts[transferNetwork.gw] = [`transfer-gw.${vpc}.exanio.net`];
  }

  // Static DNS records captured in the customer network section. Merge each
  // record's name under its target IP so it appears in the hosts mapping;
  // several records may share one IP, so append rather than overwrite.
  if (Array.isArray(customerNetwork.dnsEntries)) {
    customerNetwork.dnsEntries.forEach((entry) => {
      const name = (entry?.name ?? "").trim();
      const ip = (entry?.ip ?? "").trim();
      if (name === "" || ip === "") return;
      if (!hosts[ip]) hosts[ip] = [];
      if (!hosts[ip].includes(name)) hosts[ip].push(name);
    });
  }

  return { hosts, external_ips: external_ips.filter(Boolean) };
};

export const getFeatures = (
  obj: Record<string, any>,
  keys?: {
    private: string;
    public: string;
  },
): OpsanioFeatures => {
  if (!obj || typeof obj !== "object") {
    console.warn("getAgents: obj is null, undefined, or not an object", {
      obj,
    });
    return {
      certificatesManagement: true,
      guacamole: false,
      networkFlows: true,
      nuclei: true,
      openVas: false,
      siem: true,
      compliance: false,
      webPush: {
        enabled: false,
        privateKey: keys.private ?? "",
        publicKey: keys.public ?? "",
      },
      opsanioAr: false,
      opsanioAgent: true,
      bgp: false,
      ai: false,
      developer: false,
      previewAccess: false,
      manageViaCapi: false,
      customDomain: false,
      aptDists: false,
      opensearchTiers: { warm: false, cold: false },
    };
  }

  const values = getItemsByKeyPrefix(obj, "features.enable.");

  const parsed: OpsanioFeatures = {
    networkFlows: getYesNoAsBoolean(values.flows) ?? true,
    siem: getYesNoAsBoolean(values.wazuh) ?? true,
    nuclei: getYesNoAsBoolean(values.nuclei) ?? true,
    openVas: getYesNoAsBoolean(values.openvas) ?? false,
    guacamole: getYesNoAsBoolean(values.guacamole) ?? false,
    certificatesManagement: getYesNoAsBoolean(values.certificates) ?? true,
    compliance: getYesNoAsBoolean(values.compliance) ?? false,
    webPush: {
      enabled: getYesNoAsBoolean(values.webPush) ?? false,
      privateKey: keys.private,
      publicKey: keys.public,
    },
    opsanioAr: getYesNoAsBoolean(values.opsanioar) ?? false,
    // On unless explicitly answered No: the agent is standard, and an old
    // submission that never saw the question must not lose it.
    opsanioAgent: getYesNoAsBoolean(values.opsanioAgent) ?? true,
    bgp: getYesNoAsBoolean(values.bgp) ?? false,
    ai: getYesNoAsBoolean(values.aiMl) ?? false,
    developer: getYesNoAsBoolean(values.developer) ?? false,
    previewAccess: getYesNoAsBoolean(values.previewAccess) ?? false,
    // Terraform-managed is the default: only an explicit Yes moves a VPC to the
    // CAPI lifecycle (no 200/210 projects, Route53 credential from Vault).
    manageViaCapi: getYesNoAsBoolean(values.manageViaCapi) ?? false,
    // Off unless asked for: everything else keeps working on
    // `<vpc>.exanio.net`, and `customerSettings.customerTld` carries the name.
    customDomain: getYesNoAsBoolean(values.customDomain) ?? false,
    // Off unless explicitly answered Yes: pinning changes where the appliance
    // gets its packages from, so it is switched on per appliance, never by default.
    aptDists: getYesNoAsBoolean(values.aptDists) ?? false,
    // Hot is the only tier now (opsanio/documentation#448): unanswered (old
    // submissions) is false too, which scales warm and cold to zero replicas.
    // Only an explicit Yes keeps a tier running.
    opensearchTiers: {
      warm: getYesNoAsBoolean(values.opensearchWarm) ?? false,
      cold: getYesNoAsBoolean(values.opensearchCold) ?? false,
    },
  };
  return parsed;
};

/**
 * The monthly cap is the ANCHOR of the whole budget block.
 *
 * An operator agrees one number with a customer - "AI costs up to 400 € a month"
 * - and the shorter windows fall out of it: they exist to catch a runaway day or
 * week early, not to be negotiated separately. So daily and weekly limits are
 * derived from the monthly one unless someone overrides them explicitly.
 */
export const AI_DEFAULT_MONTHLY_LIMIT = 400;

/** Days per month used to derive the daily cap. A flat 30, not the calendar. */
export const AI_DAILY_DIVISOR = 30;
/** Weeks per month used to derive the weekly cap. */
export const AI_WEEKLY_DIVISOR = 4;
/**
 * Where the alert threshold sits under its cap, as a fraction.
 *
 * 90%: close enough that crossing it means the cap really is about to be hit
 * (and the forecast alert has something to forecast against), far enough that
 * there is still room to act before requests are affected.
 */
export const AI_SOFT_RATIO = 0.9;

/** Round to cents. Deriving 400/30 otherwise yields 13.333333333333334 €. */
const toCents = (value: number): number => Math.round(value * 100) / 100;

/** The daily cap implied by a monthly one. */
export const aiDailyLimitFor = (monthlyLimit: number): number =>
  toCents(monthlyLimit / AI_DAILY_DIVISOR);

/** The weekly cap implied by a monthly one. */
export const aiWeeklyLimitFor = (monthlyLimit: number): number =>
  toCents(monthlyLimit / AI_WEEKLY_DIVISOR);

/** The alert threshold implied by a cap. */
export const aiSoftLimitFor = (limit: number): number =>
  toCents(limit * AI_SOFT_RATIO);

/**
 * Fallback AI settings - the answer when the questionnaire has no AI facts at
 * all (an old submission, or a VPC whose AI section was never opened).
 *
 * Derived from {@link AI_DEFAULT_MONTHLY_LIMIT} rather than written out, so the
 * three windows cannot drift apart and the questionnaire's seeded values (which
 * read this same function) cannot drift from the parser's.
 *
 * Budgets and alerting are ON: an unanswered questionnaire must not quietly
 * authorise an open-ended bill.
 *
 * Only the MONTHLY window rejects. The shorter windows alert instead, because
 * they are derived rather than agreed - a daily cap that nobody negotiated should
 * not be able to take AI away for the rest of the day. The monthly cap is the one
 * a customer actually signed up to, so it is the one that holds.
 */
export const getDefaultAiSettings = (): OpsanioAi => {
  const monthly = AI_DEFAULT_MONTHLY_LIMIT;
  const daily = aiDailyLimitFor(monthly);
  const weekly = aiWeeklyLimitFor(monthly);
  return {
    budgets: {
      enabled: true,
      daily: { limit: daily, soft: aiSoftLimitFor(daily), enforce: false },
      weekly: { limit: weekly, soft: aiSoftLimitFor(weekly), enforce: false },
      monthly: { limit: monthly, soft: aiSoftLimitFor(monthly), enforce: true },
    },
    // Filled in at render time by enrichWithAiAccess, which provisions the
    // Keycloak client and only then knows the id and secret. Empty means "no
    // SSO", which the chart reads as "fall back to the UI username/password".
    access: {
      oidc: {
        enabled: true,
        issuer: "",
        clientId: "",
        clientSecret: "",
        scope: "openid profile email",
        roleClaim: "litellm_role",
        proxyBaseUrl: "",
      },
    },
    alerting: {
      enabled: true,
      // Empty means "use the default": the per-VPC override is a questionnaire
      // answer, and when it is blank enrichWithAiAlerting fills in the
      // admin-managed destination instead. See getAiAlertWebhookOverride.
      rocketChatWebhookUrl: "",
      // Never a questionnaire answer: anketa builds its own receiver URL, and it
      // carries the shared secret that authenticates the gateway to it.
      anketaWebhookUrl: "",
      reportFrequency: "7d",
      email: {
        enabled: false,
        host: "",
        port: 587,
        username: "",
        password: "",
        senderEmail: "",
        receiverEmails: [],
      },
    },
  };
};

/**
 * Parse one budget window out of the `ai.budgets.*` facts.
 *
 * A blank or unparseable limit falls back to `fallback.limit` rather than to
 * zero: zero is a legitimate LiteLLM budget meaning "reject everything", so
 * treating a cleared field as 0 would take AI away from a customer who merely
 * left an answer empty. Same reasoning as the retention parser's blank handling.
 *
 * An absent threshold is derived from the EFFECTIVE limit
 * ({@link AI_SOFT_RATIO}), not carried over from the fallback: an operator who
 * raises the monthly cap to 900 and leaves the threshold blank wants to hear
 * about it at 810, not at the 360 that belonged to the old cap. A threshold at or
 * above its cap is replaced for the same reason - it would only fire once
 * requests were already being rejected.
 */
const getAiBudgetWindow = (
  values: Record<string, any>,
  prefix: string,
  fallback: OpsanioAiBudgetWindow,
): OpsanioAiBudgetWindow => {
  const number = (raw: any, dflt: number): number => {
    if (raw === undefined || raw === null || `${raw}`.trim() === "")
      return dflt;
    const parsed = Number(`${raw}`.replace(",", ".").trim());
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : dflt;
  };

  const limit = number(values[`${prefix}Limit`], fallback.limit);
  const derived = aiSoftLimitFor(limit);
  const softRaw = number(values[`${prefix}Soft`], derived);
  const soft = softRaw >= limit ? derived : softRaw;
  // Only an explicit answer moves enforcement; an unanswered window keeps its
  // own default, which is what leaves monthly hard and the derived windows soft.
  const enforce =
    getYesNoAsBoolean(values[`${prefix}Enforce`]) ?? fallback.enforce;

  return { limit, soft, enforce };
};

/**
 * The per-VPC cost-alert webhook override, as answered in the questionnaire.
 *
 * Returns "" for anything unusable, which the render path reads as "fall back to
 * the admin-managed destination" - so a half-typed URL degrades to the default
 * room rather than to no alerting at all.
 *
 * https ONLY. The URL is a bearer credential (whoever holds it can post into that
 * room) and the payload names a customer and what they spent, so it must not
 * travel in cleartext. An http:// answer is refused rather than downgraded
 * silently, matching the same rule the Admin settings endpoint enforces.
 */
export const getAiAlertWebhookOverride = (raw: unknown): string => {
  const value = `${raw ?? ""}`.trim();
  if (value === "") return "";
  try {
    return new URL(value).protocol === "https:" ? value : "";
  } catch {
    return "";
  }
};

/**
 * Recipients for the gateway's e-mail alert channel, from a free-text answer.
 *
 * Accepts comma, semicolon, newline or whitespace separation, because a list of
 * addresses gets pasted out of a mail client as often as it gets typed. Lowercased
 * and de-duplicated, so the same person named twice is mailed once.
 *
 * Anything that is not an address is DROPPED rather than passed through. LiteLLM
 * hands the whole list to its SMTP callback, and one malformed entry can make a
 * server reject the message for every recipient - a name where an address was
 * expected must not cost the other people their alert.
 *
 * Unlike the webhook URL these are not credentials, which is why they are a
 * questionnaire answer rather than an admin-only setting.
 */
export const getAiAlertRecipients = (raw: unknown): string[] => {
  const value = `${raw ?? ""}`.trim();
  if (value === "") return [];
  const seen = new Set<string>();
  for (const candidate of value.split(/[,;\s]+/)) {
    const address = candidate.trim().toLowerCase();
    if (address !== "" && isValidEmail(address)) {
      seen.add(address);
    }
  }
  return [...seen];
};

/**
 * AI gateway settings from the questionnaire's internal-only AI section.
 *
 * Every figure is EURO - see OpsanioAi. The three windows are emitted whether or
 * not the AI feature is on: the chart gates the whole block on `features.ai`, so
 * carrying the answers for a disabled stack costs nothing and means turning AI
 * on later does not need the section re-answered.
 *
 * MONTHLY IS PARSED FIRST, deliberately: it is the anchor, and the daily and
 * weekly windows fall back to fractions of whatever it resolved to. So raising
 * the monthly cap alone moves all three, while an explicit daily or weekly answer
 * still wins over the derivation.
 *
 * One alert destination IS read from the facts: the per-VPC cost-alert webhook,
 * so a customer's budget events can be routed to their own room without an admin
 * round-trip. Blank means "use the admin-managed default".
 *
 * Be aware of what that implies: the fact table is not encrypted, and the
 * `hideForExternal` gap means an external member of this questionnaire can read
 * it back over the API. Treat that URL as disclosed to whoever can read the
 * questionnaire, and keep genuinely sensitive rooms on the admin-managed setting.
 *
 * The other destinations stay out of the facts entirely: the SMTP password
 * obviously, and anketa's own receiver URL because it carries the shared secret
 * that authenticates the gateway to it. Both are injected at render time - see
 * enrichWithAiAlerting.
 */
export const getAiSettings = (obj: Record<string, any>): OpsanioAi => {
  const defaults = getDefaultAiSettings();
  if (!obj || typeof obj !== "object") return defaults;

  const values = getItemsByKeyPrefix(obj, "ai.budgets.");
  const alertValues = getItemsByKeyPrefix(obj, "ai.alerting.");

  const monthly = getAiBudgetWindow(
    values,
    "monthly",
    defaults.budgets.monthly,
  );
  // The derived windows track the monthly cap that was actually answered.
  const daily = getAiBudgetWindow(values, "daily", {
    ...defaults.budgets.daily,
    limit: aiDailyLimitFor(monthly.limit),
  });
  const weekly = getAiBudgetWindow(values, "weekly", {
    ...defaults.budgets.weekly,
    limit: aiWeeklyLimitFor(monthly.limit),
  });

  return {
    budgets: {
      // Only an explicit No leaves the gateway uncapped. An unanswered
      // questionnaire keeps the caps - failing open here means an unbounded bill.
      enabled: getYesNoAsBoolean(values.enabled) ?? defaults.budgets.enabled,
      daily,
      weekly,
      monthly,
    },
    // Carried through unchanged: there are no facts for it. enrichWithAiAccess
    // replaces it at render time once the Keycloak client exists.
    access: defaults.access,
    alerting: {
      ...defaults.alerting,
      enabled:
        getYesNoAsBoolean(alertValues.enabled) ?? defaults.alerting.enabled,
      reportFrequency:
        `${alertValues.reportFrequency ?? ""}`.trim() ||
        defaults.alerting.reportFrequency,
      rocketChatWebhookUrl: getAiAlertWebhookOverride(alertValues.webhookUrl),
      email: {
        ...defaults.alerting.email,
        // The only e-mail field that is an answer. Host, user and password are
        // credentials/infrastructure and come from the environment at render
        // time; an empty list there means the channel falls back to
        // AI_ALERT_EMAIL_RECIPIENTS.
        receiverEmails: getAiAlertRecipients(alertValues.receiverEmails),
      },
    },
  };
};

/** Compliance frameworks archived forever by default (never cut/deleted). */
export const DEFAULT_KEEP_COMPLIANCE_GROUPS = [
  "gdpr",
  "pci_dss",
  "hipaa",
  "nist_800_53",
  "tsc",
  "gpg13",
];

/**
 * Fully-defaulted retention tree - the answer when no facts are present.
 * Lifecycle management is on by default for events, devices (assets) and flows
 * (opsanio/documentation#448); only an explicit No leaves an index unmanaged.
 * Events, devices (merged) and flows (aggregate) keep 90d by default.
 */
export const getDefaultRetentions = (): OpsanioRetentions => ({
  events: {
    manageLifecycle: true,
    severityCuts: [{ forceMergeAge: "1d", keepLevel: 2 }],
    retentionAge: "90d",
    keepComplianceGroups: [...DEFAULT_KEEP_COMPLIANCE_GROUPS],
    // Not customer-facing: default backstop is 2× retentionAge.
    hardCapAge: "180d",
    indexPattern: "wazuh-alerts-*",
    maintenanceInterval: "6h",
  },
  devices: {
    manageLifecycle: true,
    merged: { retentionAge: "90d", forceMergeAge: "1d" },
    raw: { retentionAge: "4d", forceMergeAge: "1d" },
  },
  flows: {
    manageLifecycle: true,
    aggregate: { retentionAge: "90d", forceMergeAge: "1d" },
    raw: { retentionAge: "7d", forceMergeAge: "1d" },
  },
});

/** Trim a duration string, falling back to `fallback` when blank/absent. */
const duration = (val: any, fallback: string): string => {
  const s = typeof val === "string" ? val.trim() : "";
  return s !== "" ? s : fallback;
};

/**
 * Double the numeric part of a duration string keeping its unit ("365d" →
 * "730d"). Used to derive the hardCapAge backstop from retentionAge.
 */
const doubleDuration = (val: string, fallback: string): string => {
  const m = /^(\d+)\s*([a-zA-Z]+)$/.exec(val.trim());
  if (!m) return fallback;
  return `${parseInt(m[1], 10) * 2}${m[2]}`;
};

/**
 * Parse the compact "AGE:LEVEL" comma-list captured by the severity-cuts field
 * into the chart's step ladder, e.g. "14d:3, 90d:7" →
 * [{forceMergeAge:"14d",keepLevel:3},{forceMergeAge:"90d",keepLevel:7}].
 */
const parseSeverityCuts = (
  val: any,
): OpsanioRetentions["events"]["severityCuts"] => {
  if (typeof val !== "string" || val.trim() === "") return [];
  return val
    .split(",")
    .map((token) => token.trim())
    .filter((token) => token !== "")
    .map((token) => {
      const [age, level] = token.split(":").map((p) => p.trim());
      const keepLevel = parseInt(level, 10);
      return { forceMergeAge: age, keepLevel };
    })
    .filter((cut) => cut.forceMergeAge && Number.isFinite(cut.keepLevel));
};

export const getRetentions = (obj: Record<string, any>): OpsanioRetentions => {
  const defaults = getDefaultRetentions();

  if (!obj || typeof obj !== "object") {
    return defaults;
  }

  const toGroups = (v: any): string[] => {
    if (Array.isArray(v)) {
      return v.map((x) => String(x)).filter((s) => s.trim() !== "");
    }
    if (typeof v === "string" && v.trim() !== "") {
      return v
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s !== "");
    }
    return [];
  };

  const events = getItemsByKeyPrefix(obj, "retentions.events.");
  const devices = getItemsByKeyPrefix(obj, "retentions.devices.");
  const flows = getItemsByKeyPrefix(obj, "retentions.flows.");

  const severityCuts = parseSeverityCuts(events.severityCuts);
  const complianceGroups = toGroups(events.complianceGroups);
  const eventsRetentionAge = duration(
    events.retentionAge,
    defaults.events.retentionAge,
  );

  const parsed: OpsanioRetentions = {
    events: {
      manageLifecycle:
        getYesNoAsBoolean(events.manageLifecycle) ??
        defaults.events.manageLifecycle,
      severityCuts:
        severityCuts.length > 0 ? severityCuts : defaults.events.severityCuts,
      retentionAge: eventsRetentionAge,
      keepComplianceGroups:
        complianceGroups.length > 0
          ? complianceGroups
          : defaults.events.keepComplianceGroups,
      // Blank ⇒ derive the 2× retentionAge backstop.
      hardCapAge: duration(
        events.hardCapAge,
        doubleDuration(eventsRetentionAge, defaults.events.hardCapAge),
      ),
      indexPattern: duration(events.indexPattern, defaults.events.indexPattern),
      maintenanceInterval: duration(
        events.maintenanceInterval,
        defaults.events.maintenanceInterval,
      ),
    },
    devices: {
      manageLifecycle:
        getYesNoAsBoolean(devices.manageLifecycle) ??
        defaults.devices.manageLifecycle,
      merged: {
        retentionAge: duration(
          devices.mergedRetentionAge,
          defaults.devices.merged.retentionAge,
        ),
        forceMergeAge: duration(
          devices.mergedForceMergeAge,
          defaults.devices.merged.forceMergeAge,
        ),
      },
      raw: {
        retentionAge: duration(
          devices.rawRetentionAge,
          defaults.devices.raw.retentionAge,
        ),
        forceMergeAge: duration(
          devices.rawForceMergeAge,
          defaults.devices.raw.forceMergeAge,
        ),
      },
    },
    flows: {
      manageLifecycle:
        getYesNoAsBoolean(flows.manageLifecycle) ??
        defaults.flows.manageLifecycle,
      aggregate: {
        retentionAge: duration(
          flows.aggregateRetentionAge,
          defaults.flows.aggregate.retentionAge,
        ),
        forceMergeAge: duration(
          flows.aggregateForceMergeAge,
          defaults.flows.aggregate.forceMergeAge,
        ),
      },
      raw: {
        retentionAge: duration(
          flows.rawRetentionAge,
          defaults.flows.raw.retentionAge,
        ),
        forceMergeAge: duration(
          flows.rawForceMergeAge,
          defaults.flows.raw.forceMergeAge,
        ),
      },
    },
  };

  return parsed;
};

/**
 * Derive the pre-sales scoping / onboarding object from the raw questionnaire
 * facts (appliance.content). This is display-only data for the appliance
 * dashboard and is deliberately kept OUT of getAssemblySite / OpsanioValues, so
 * the generated helm values.yaml is unaffected.
 *
 * Reads five content sections:
 *   - general.information.*       -> PoC / Reference flags
 *   - scanners.general.*          -> security products + sizing scoping
 *   - networks.netzwerk_fragen.*  -> network inventory scoping
 *   - comment.comment_<n>         -> free-text questions/comments
 *   - cpe.location                -> all sites (primary first)
 *
 * Tolerant of missing facts: every field is optional and falls back to an empty
 * value, so a partially-filled questionnaire still yields a valid object.
 */
export const getOpsanioScoping = (obj: Record<string, any>): OpsanioScoping => {
  const empty: OpsanioScoping = {
    poc: { wantPoc: false, allowedAsRefCustomer: false, pocSuccess: false },
    scanners: {
      security: { edr: [], emailSecurity: [], browserSecurity: [] },
      servers: { physicalOs: [], virtualPlatform: [] },
    },
    network: { firewalls: [], switches: [] },
    notes: [],
    locations: [],
  };

  if (!obj || typeof obj !== "object") {
    return empty;
  }

  // Multi-select facts persist as string[] (QuestionObject<string[]>); tolerate
  // a comma-joined string too.
  const toStringArray = (v: any): string[] => {
    if (Array.isArray(v)) {
      return v.map((x) => String(x)).filter((s) => s.trim() !== "");
    }
    if (typeof v === "string" && v.trim() !== "") {
      return v
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s !== "");
    }
    return [];
  };
  const toNumber = (v: any): number | undefined => {
    if (v === undefined || v === null || v === "") return undefined;
    const n = parseFloat(v);
    return Number.isNaN(n) ? undefined : n;
  };

  // --- PoC / Reference (general.information.*) -----------------------------
  const general = getItemsByKeyPrefix(obj, "general.");
  const poc: OpsanioPoc = {
    allowedAsRefCustomer:
      getYesNoAsBoolean(general["information.ref"]) || false,
    wantPoc: getYesNoAsBoolean(general["information.poc"]) || false,
    pocSuccess: getYesNoAsBoolean(general["information.pocSuccess"]) || false,
  };

  // --- Scanner scoping (scanners.general.*) --------------------------------
  const sg = getItemsByKeyPrefix(obj, "scanners.general.");
  const scanners: OpsanioScoping["scanners"] = {
    security: {
      edr: toStringArray(sg.edr),
      emailSecurity: toStringArray(sg.emailSecurity),
      browserSecurity: toStringArray(sg.browserSecurity),
      m365: getYesNoAsBoolean(sg.m365),
    },
    servers: {
      physicalCount: toNumber(sg.physischeServer),
      physicalOs: toStringArray(sg.physischeServerOs),
      virtualCount: toNumber(sg.virtuellerServer),
      virtualPlatform: toStringArray(sg.virtuellerServerPlatform),
    },
    endpoints: toNumber(sg.endpoints),
    users: toNumber(sg.user),
    logSources: toNumber(sg.logQuellen),
    appLogging: getYesNoAsBoolean(sg.appLogging),
  };

  // --- Network scoping (networks.netzwerk_fragen.*) ------------------------
  const nf = getItemsByKeyPrefix(obj, "networks.netzwerk_fragen.");
  const network: OpsanioScoping["network"] = {
    firewalls: toStringArray(nf.firewalls),
    switches: toStringArray(nf.switche),
    fcVsCopper: getYesNoAsBoolean(nf.anbindungFcKupfer),
    additionalComponents:
      typeof nf.weitereNetzwerkkomponenten === "string" &&
      nf.weitereNetzwerkkomponenten.trim() !== ""
        ? nf.weitereNetzwerkkomponenten
        : undefined,
    separateVlansZones: getYesNoAsBoolean(nf.vlansZonen),
    collectFlowData: getYesNoAsBoolean(nf.flowDaten),
  };

  // --- Notes (comment.comment_<n>) -----------------------------------------
  // Read raw facts directly: getItemsByKeyPrefix only keeps `.value`, but the
  // CommentQuestion fact also carries user / commentType / createTime.
  const notes: OpsanioScopingNote[] = Object.entries(obj)
    .filter(([key]) => /^comment\.comment_\d+$/.test(key))
    .map(([key, fact]) => {
      const f: any = fact ?? {};
      const rawText = typeof f === "string" ? f : f?.value;
      return {
        id: key,
        text: typeof rawText === "string" ? rawText : String(rawText ?? ""),
        user: typeof f?.user === "string" ? f.user : undefined,
        commentType: f?.commentType,
        createTime:
          typeof f?.createTime === "string" ? f.createTime : undefined,
      };
    })
    .filter((n) => n.text.trim() !== "")
    .sort((a, b) => {
      const ai = parseInt(a.id.split("_").pop() || "0", 10);
      const bi = parseInt(b.id.split("_").pop() || "0", 10);
      return ai - bi;
    });

  // --- All locations (cpe.location) ----------------------------------------
  // cpe.location persists as a JSON-stringified array of sites; getAssemblySite
  // collapses it to the single primary site for common.location / values.yaml.
  // Here we keep every site (primary first) so no location data is lost.
  const locationFact = obj["cpe.location"];
  const locationRaw =
    locationFact === null || locationFact === undefined
      ? undefined
      : typeof locationFact === "string"
        ? locationFact
        : locationFact.value;
  let locations: OpsanioLocationValue[] = [];
  if (Array.isArray(locationRaw)) {
    locations = locationRaw as OpsanioLocationValue[];
  } else if (typeof locationRaw === "string" && locationRaw.trim() !== "") {
    try {
      const parsed = JSON.parse(locationRaw);
      if (Array.isArray(parsed)) {
        locations = parsed as OpsanioLocationValue[];
      } else if (parsed) {
        locations = [parsed as OpsanioLocationValue];
      }
    } catch {
      locations = [];
    }
  }
  locations = [...locations].sort(
    (a, b) => Number(!!b.isPrimary) - Number(!!a.isPrimary),
  );

  return { poc, scanners, network, notes, locations };
};
